import React,{useState,useEffect} from "react";
import {C,fmt,todayStr,toLocalDateStr} from "../utils/constants";

// ── Constants ─────────────────────────────────────────────────────
const PLANS=[
  {id:"starter",  name:"Starter",    color:"#64748B", price:299,  users:"1+2",  icon:"⭐"},
  {id:"basic",    name:"Basic",      color:"#2563EB", price:499,  users:"1+5",  icon:"🚀"},
  {id:"pro",      name:"Pro",        color:"#7C3AED", price:999,  users:"3+8",  icon:"💎"},
  {id:"enterprise",name:"Enterprise",color:"#B45309", price:1999, users:"∞",    icon:"🏢"},
];
const DURATIONS=[
  {months:1,  label:"1 Month",   disc:0},
  {months:2,  label:"2 Months",  disc:0},
  {months:3,  label:"3 Months",  disc:5},
  {months:6,  label:"6 Months",  disc:10},
  {months:12, label:"1 Year",    disc:20},
];
const WA_TEMPLATES={
  welcome:(t,hl)=>`🎉 *Welcome to DawnToDusk Pro!*\n\nDear *${t.owner_name||t.name}*,\n\nYour account is ready!\n\n👤 Username: ${t.username||"—"}\n🔑 Password: (as set)\n🌐 Login: dawntoduskpro.com\n\n📞 Help: ${hl.phone||""}\n💬 WA: ${hl.whatsapp||""}\n\nThank you! 🙏\nTeam DawnToDusk Pro`,
  trial:(t,days,hl)=>`⏰ *Trial Expiry Reminder*\n\nDear *${t.owner_name||t.name}*,\n\nYour DawnToDusk Pro trial expires in *${days} days*.\n\n✨ Subscribe today:\n⭐ Starter: ₹299/month\n🚀 Basic: ₹499/month\n💎 Pro: ₹999/month\n\n📞 ${hl.phone||""}\n💬 WA: ${hl.whatsapp||""}\n\nThank you! 🙏`,
  payment:(t,inv,amt,plan,months,expiry,hl)=>`✅ *Payment Received!*\n\nDear *${t.owner_name||t.name}*,\n\n📋 Invoice: ${inv}\n💰 Amount: ₹${fmt(amt)}\n📦 Plan: ${plan}\n⏱️ Duration: ${months} month${months>1?"s":""}\n📅 Valid Till: ${expiry}\n\n✅ Your account is active!\n\n📞 ${hl.phone||""}\nTeam DawnToDusk Pro`,
  renewal:(t,days,hl)=>`🔔 *Renewal Reminder*\n\nDear *${t.owner_name||t.name}*,\n\nYour subscription expires in *${days} days*.\n\nTo renew, contact us:\n📞 ${hl.phone||""}\n💬 WA: ${hl.whatsapp||""}\n\nThank you! 🙏`,
  expired:(t,hl)=>`⚠️ *Account Expired*\n\nDear *${t.owner_name||t.name}*,\n\nYour DawnToDusk Pro account has expired.\n\nSubscribe now to continue:\n📞 ${hl.phone||""}\n💬 WA: ${hl.whatsapp||""}\n\nThank you! 🙏`,
};

function calcAmt(planId,months,pricing){
  const p=PLANS.find(x=>x.id===planId);
  if(!p)return 0;
  const unitPrice=pricing?.[planId]?.price||p.price;
  const base=unitPrice*months;
  const d=DURATIONS.find(x=>x.months===months);
  return Math.round(base*(1-(d?.disc||0)/100));
}
function calcExpiry(months, currentExpiry){
  // Agar current expiry future mein hai → wahan se extend
  // Agar expired/trial → aaj se count
  const today=new Date();
  const curr=currentExpiry?new Date(currentExpiry):null;
  const base=(curr&&curr>today)?curr:today;
  const d=new Date(base);
  d.setMonth(d.getMonth()+months);
  return toLocalDateStr(d);
}
function daysLeft(dateStr){
  if(!dateStr)return 0;
  return Math.ceil((new Date(dateStr)-new Date())/(864e5));
}
function openWA(phone,msg){
  const ph=(phone||"").replace(/\D/g,"");
  if(!ph)return;
  window.open(`https://wa.me/91${ph}?text=${encodeURIComponent(msg)}`,"_blank");
}

// ── Sub Components ────────────────────────────────────────────────
function StatCard({icon,label,value,color,onClick}){
  return(
    <div onClick={onClick} style={{background:"#fff",borderRadius:12,padding:"12px 8px",textAlign:"center",border:`2px solid ${color}20`,cursor:onClick?"pointer":"default",flex:1}}>
      <div style={{fontSize:18}}>{icon}</div>
      <div style={{fontSize:20,fontWeight:800,color,marginTop:2}}>{value}</div>
      <div style={{fontSize:10,color:C.sub,marginTop:1}}>{label}</div>
    </div>
  );
}

// Simple dependency-free bar chart — takes [{label, value}] and renders proportional bars
function MonthBarChart({data,color,valuePrefix=""}){
  const max=Math.max(1,...data.map(d=>d.value));
  return(
    <div style={{display:"flex",alignItems:"flex-end",gap:6,height:120,padding:"8px 4px"}}>
      {data.map((d,i)=>(
        <div key={i} style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",gap:4}}>
          <div style={{fontSize:9,fontWeight:700,color}}>{d.value>0?valuePrefix+d.value:""}</div>
          <div style={{width:"100%",height:Math.max(3,(d.value/max)*80),background:color,borderRadius:"4px 4px 0 0",transition:"height 0.3s"}}/>
          <div style={{fontSize:9,color:C.sub,fontWeight:600}}>{d.label}</div>
        </div>
      ))}
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────
export default function SuperAdminPanel({
  superAdmin,tenants,loadTenants,saTab,setSaTab,
  saForm,setSaForm,saMsg,setSaMsg,
  announcement,setAnnouncement,
  onLogout,showToast,API,Inp,Btn,Card,impersonateTenant
}){
  // ── States ────────────────────────────────────────────────────
  const [pricing,setPricing]=useState(()=>{try{return JSON.parse(localStorage.getItem("sa_pricing")||"null")||{trial_days:7,starter:{price:299},basic:{price:499},pro:{price:999},enterprise:{price:1999}};}catch{return{trial_days:7,starter:{price:299},basic:{price:499},pro:{price:999},enterprise:{price:1999}};}});
  const [helpline,setHelpline]=useState(()=>{try{return JSON.parse(localStorage.getItem("sa_helpline")||"null")||{phone:"6284393851",whatsapp:"6284393851",email:"",hours:"Mon-Sat 10AM-6PM",msg:""};}catch{return{phone:"6284393851",whatsapp:"6284393851",email:"",hours:"Mon-Sat 10AM-6PM",msg:""};}});
  useEffect(()=>{
    (async()=>{
      try{
        const srv=await API.get("/helpline");
        if(srv&&srv.phone){setHelpline(srv);localStorage.setItem("sa_helpline",JSON.stringify(srv));}
      }catch(e){}
      try{
        const srvP=await API.get("/pricing");
        if(srvP&&Object.keys(srvP).length){setPricing(p=>({...p,...srvP}));localStorage.setItem("sa_pricing",JSON.stringify(srvP));}
      }catch(e){}
      try{
        const srvO=await API.get("/offers");
        if(Array.isArray(srvO)){setOffers(srvO);localStorage.setItem("sa_offers",JSON.stringify(srvO));}
      }catch(e){}
    })();
  },[]);

  // ── Super Admin Credentials Change ──
  const [credForm,setCredForm]=useState({currentPassword:"",newUsername:"",newPassword:"",confirmPassword:""});
  const [credShow,setCredShow]=useState(false);
  const [credLoading,setCredLoading]=useState(false);
  const [offers,setOffers]=useState(()=>{try{return JSON.parse(localStorage.getItem("sa_offers")||"[]");}catch{return[];}});
  const [offerForm,setOfferForm]=useState({type:"percent"});
  const [allPayments,setAllPayments]=useState([]);
  const [tenantStats,setTenantStats]=useState({});
  const [payHistory,setPayHistory]=useState({});
  const [selTenant,setSelTenant]=useState(null);
  const [showPayModal,setShowPayModal]=useState(false);
  const [showEditModal,setShowEditModal]=useState(false);
  const [showProfileModal,setShowProfileModal]=useState(false);
  const [showTemplates,setShowTemplates]=useState(false);
  const [payForm,setPayForm]=useState({plan:"basic",months:1,method:"Cash",note:"",customAmt:""});
  const [editForm,setEditForm]=useState({});
  const [srch,setSrch]=useState("");
  const [filterStatus,setFilterStatus]=useState("all");
  const [sortBy,setSortBy]=useState("expiry");
  const [editPrice,setEditPrice]=useState(false);
  const [loading,setLoading]=useState(false);

  const IS={width:"100%",padding:"10px 12px",borderRadius:9,border:`1.5px solid ${C.border}`,fontSize:13,fontFamily:"inherit",outline:"none",background:"#F8FAFC",boxSizing:"border-box"};

  // ── Load Data ────────────────────────────────────────────────
  useEffect(()=>{
    loadPayments();
  },[]);

  const loadPayments=async()=>{
    try{const d=await API.get("/sa/payments");if(Array.isArray(d))setAllPayments(d);}catch(e){}
  };
  const loadTenantStats=async(tid)=>{
    try{const s=await API.get(`/tenants/${tid}/stats`);setTenantStats(p=>({...p,[tid]:s}));}catch(e){}
  };
  const loadPayHistory=async(tid)=>{
    try{const h=await API.get(`/sa/payments/${tid}`);if(Array.isArray(h))setPayHistory(p=>({...p,[tid]:h}));}catch(e){}
  };

  // ── Add Tenant ───────────────────────────────────────────────
  const addTenant=async()=>{
    if(!saForm.name||!saForm.phone){setSaMsg("❌ Name and phone are required!");return;}
    setLoading(true);
    try{
      const r=await API.post("/tenants",{...saForm,trial_days:pricing.trial_days||7});
      setSaMsg(`✅ Added!\n👤 Login: ${r.username}\n🔑 Pass: ${r.password}`);
      setSaForm({});loadTenants();loadPayments();
      // Welcome WA
      setTimeout(()=>{
        if(saForm.phone){
          const t={...saForm,owner_name:saForm.owner_name,username:r.username};
          openWA(saForm.phone,WA_TEMPLATES.welcome(t,helpline));
        }
      },1000);
    }catch(e){setSaMsg("❌ "+e.message);}
    setLoading(false);
  };

  // ── Edit Tenant ──────────────────────────────────────────────
  const saveTenantEdit=async()=>{
    setLoading(true);
    try{
      const payload={...editForm};
      if(editForm.newPassword&&editForm.newPassword.trim()){
        payload.password=editForm.newPassword.trim();
      }
      delete payload.newPassword;
      await API.put(`/tenants/${editForm.id}`,payload);
      showToast("✅ Distributor updated!"+(editForm.newPassword?" Password bhi change!":""));
      setShowEditModal(false);setEditForm({});
      loadTenants();
    }catch(e){showToast("❌ "+e.message,"err");}
    setLoading(false);
  };

  // ── Toggle Tenant ────────────────────────────────────────────
  const toggleTenant=async(t)=>{
    const ns=t.status==="active"?"inactive":"active";
    let reason="";
    if(ns==="inactive"){
      reason=window.prompt(`Why is ${t.name} being paused? (optional — helps track churn reasons)\ne.g. "Too expensive", "Switched to competitor", "Business closed"`) || "";
    }
    try{
      await API.put(`/tenants/${t.id}`,{...t,status:ns,status_reason:reason});
      loadTenants();loadStatusLog();
      showToast(ns==="active"?"✅ Activated!":"⏸ Paused!");
    }catch(e){ showToast("❌ Failed: "+(e.message||"Server error"),"err"); }
  };

  // ── Delete Tenant ────────────────────────────────────────────
  const deleteTenant=async(id)=>{
    if(!window.confirm("Are you sure you want to delete? This is permanent!"))return;
    try{
      await API.del(`/tenants/${id}`);
      loadTenants();showToast("🗑️ Deleted!");
    }catch(e){ showToast("❌ Failed: "+(e.message||"Server error"),"err"); }
  };

  // ── Record Payment ───────────────────────────────────────────
  // ── Print SA Invoice/Receipt ─────────────────────────────────
  const printSAInvoice=(tenant, invoiceNo, amount, plan, months, expiryDate, method)=>{
    const w=window.open('','_blank','width=800,height=600');
    if(!w){alert('Popup blocked!');return;}
    try{ w.moveTo(0,0); w.resizeTo(window.screen.availWidth,window.screen.availHeight); }catch(e){}
    const planName=(PLANS.find(p=>p.id===plan)||{}).name||plan;
    const planPrice=amount;
    // GST breakdown — subscription amount is treated as inclusive of 18% GST (standard for SaaS in India)
    const taxable=planPrice/1.18;
    const gstAmt=planPrice-taxable;
    const cgst=gstAmt/2, sgst=gstAmt/2;
    const sellerName=helpline.our_business_name||"DawnToDusk Pro";
    const sellerGstin=helpline.our_gstin||"";
    w.document.write(`<!DOCTYPE html><html><head><title>Invoice ${invoiceNo}</title>
    <style>
      *{margin:0;padding:0;box-sizing:border-box}
      body{font-family:Arial,sans-serif;background:#f0f0f0;padding:20px}
      .invoice{width:210mm;margin:0 auto;background:#fff;padding:15mm;box-shadow:0 2px 8px rgba(0,0,0,0.15)}
      .header{display:flex;justify-content:space-between;align-items:flex-start;border-bottom:3px solid #1C2B3A;padding-bottom:12px;margin-bottom:12px}
      .brand{font-size:22px;font-weight:900;color:#1C2B3A}
      .brand-sub{font-size:10px;color:#666;margin-top:2px}
      .inv-title{font-size:13px;font-weight:700;color:#F4A622;background:#1C2B3A;padding:4px 12px;border-radius:4px}
      .inv-no{font-size:20px;font-weight:800;color:#1C2B3A;margin-top:4px}
      .section{margin-bottom:14px;padding:10px 12px;background:#F8FAFC;border-radius:8px;border-left:3px solid #1C2B3A}
      .section-title{font-size:10px;color:#666;font-weight:700;text-transform:uppercase;margin-bottom:6px}
      .party-name{font-size:15px;font-weight:800;color:#1C2B3A}
      .party-sub{font-size:11px;color:#555;margin-top:2px}
      table{width:100%;border-collapse:collapse;margin:14px 0}
      th{background:#1C2B3A;color:#fff;padding:8px;text-align:left;font-size:11px}
      td{padding:8px;border-bottom:1px solid #eee;font-size:12px}
      .total-row{background:#F0F9FF;font-weight:800;font-size:13px}
      .total-row td{color:#1C2B3A}
      .amount-box{background:linear-gradient(135deg,#1C2B3A,#2563EB);color:#fff;border-radius:10px;padding:14px;text-align:center;margin:14px 0}
      .footer{border-top:2px solid #1C2B3A;padding-top:10px;margin-top:14px;display:flex;justify-content:space-between;font-size:10px;color:#666}
      .np{margin-bottom:12px}
      @page{size:A4;margin:0}
      @media print{body{background:#fff;padding:0}.invoice{box-shadow:none;padding:10mm}.np{display:none}}
    </style></head><body>
    <div class="np">
      <button onclick="window.print()" style="padding:8px 16px;background:#1C2B3A;color:#fff;border:none;border-radius:6px;cursor:pointer;font-size:13px;font-weight:700;margin-right:8px">🖨️ Print / PDF</button>
      <button onclick="window.close()" style="padding:8px 14px;background:#666;color:#fff;border:none;border-radius:6px;cursor:pointer">✕ Close</button>
    </div>
    <div class="invoice">
      <div class="header">
        <div>
          <div class="brand">🌅 ${sellerName}</div>
          <div class="brand-sub">DISTRIBUTION MANAGEMENT SOFTWARE</div>
          ${sellerGstin?`<div class="brand-sub">GSTIN: ${sellerGstin}</div>`:''}
          ${helpline.our_address?`<div class="brand-sub">${helpline.our_address}</div>`:''}
          <div class="brand-sub">Helpline: ${helpline.phone||helpline}</div>
        </div>
        <div style="text-align:right">
          <div class="inv-title">TAX INVOICE</div>
          <div class="inv-no">${invoiceNo}</div>
          <div style="font-size:11px;color:#666;margin-top:4px">Date: ${todayStr()}</div>
        </div>
      </div>
      
      <div class="section">
        <div class="section-title">Billed To</div>
        <div class="party-name">${tenant.name}</div>
        <div class="party-sub">${tenant.owner_name||''} | Ph: ${tenant.phone||''}</div>
        ${tenant.email?`<div class="party-sub">Email: ${tenant.email}</div>`:''}
        ${tenant.city?`<div class="party-sub">${tenant.city}</div>`:''}
        ${tenant.gstin?`<div class="party-sub">GSTIN: ${tenant.gstin}</div>`:'<div class="party-sub" style="color:#B45309">GSTIN not provided (unregistered / B2C)</div>'}
      </div>

      <table>
        <thead><tr><th>#</th><th>Description</th><th>Plan</th><th>Duration</th><th>Taxable Value</th></tr></thead>
        <tbody>
          <tr>
            <td>1</td>
            <td>DawnToDusk Pro Subscription<br/><span style="font-size:10px;color:#666">Software License - ${planName} (SAC 998365)</span></td>
            <td><b>${planName}</b></td>
            <td>${months} Month${months>1?'s':''}</td>
            <td>₹${taxable.toFixed(2)}</td>
          </tr>
        </tbody>
        <tfoot>
          <tr><td colspan="4" style="text-align:right">Taxable Value</td><td>₹${taxable.toFixed(2)}</td></tr>
          <tr><td colspan="4" style="text-align:right">CGST @ 9%</td><td>₹${cgst.toFixed(2)}</td></tr>
          <tr><td colspan="4" style="text-align:right">SGST @ 9%</td><td>₹${sgst.toFixed(2)}</td></tr>
          <tr class="total-row"><td colspan="4">Total Amount (incl. GST)</td><td>₹${planPrice.toLocaleString('en-IN')}</td></tr>
        </tfoot>
      </table>

      <div class="amount-box">
        <div style="font-size:11px;color:rgba(255,255,255,0.7);margin-bottom:4px">Amount Paid</div>
        <div style="font-size:28px;font-weight:900">₹${planPrice.toLocaleString('en-IN')}</div>
        <div style="font-size:11px;color:rgba(255,255,255,0.7);margin-top:4px">via ${method||'Cash'} | Valid till: ${expiryDate||'N/A'}</div>
      </div>

      <div class="footer">
        <div>
          <div style="font-weight:700;margin-bottom:4px">${sellerName}</div>
          <div>dawntoduskpro.com | ${helpline.phone||helpline}</div>
          <div style="margin-top:4px;font-style:italic">This is a computer-generated tax invoice. Thank you for your subscription!</div>
        </div>
        <div style="text-align:right">
          <div style="font-weight:700">Authorised Signature</div>
          <div style="margin-top:20px;border-top:1px solid #999;padding-top:4px">${sellerName}</div>
        </div>
      </div>
    </div>
    </body></html>`);
    w.document.close();
  };

  const recordPayment=async()=>{
    if(!selTenant){return;}
    setLoading(true);
    try{
      const amt=payForm.customAmt?+payForm.customAmt:calcAmt(payForm.plan,payForm.months,pricing);
      const expiry=calcExpiry(payForm.months, selTenant.trial_end);
      const r=await API.post("/sa/payments",{
        tenant_id:selTenant.id,
        amount:amt,
        plan:payForm.plan,
        duration_months:payForm.months,
        method:payForm.method,
        note:payForm.note,
      });
      await loadPayments();
      await loadPayHistory(selTenant.id);
      loadTenants();
      setShowPayModal(false);
      showToast(`✅ Payment saved! Invoice: ${r.invoice_no}`);
      // Print Invoice
      const planInfo=PLANS.find(p=>p.id===payForm.plan);
      printSAInvoice(selTenant, r.invoice_no, amt, payForm.plan, payForm.months, r.expiry_date, payForm.method);
      // WA Receipt
      openWA(selTenant.phone,WA_TEMPLATES.payment(
        selTenant,r.invoice_no,amt,planInfo?.name||payForm.plan,
        payForm.months,r.expiry_date,helpline
      ));
    }catch(e){showToast("❌ "+e.message,"err");}
    setLoading(false);
  };

  // ── Extend Trial ─────────────────────────────────────────────
  const extendTrial=async(t,days)=>{
    const curr=new Date(t.trial_end||new Date());
    curr.setDate(curr.getDate()+days);
    await API.put(`/tenants/${t.id}`,{...t,trial_end:toLocalDateStr(curr),status:"active"});
    loadTenants();showToast(`✅ Trial extended by ${days} days!`);
  };

  // ── Save Settings ────────────────────────────────────────────
  const savePricing=async()=>{
    localStorage.setItem("sa_pricing",JSON.stringify(pricing));
    setEditPrice(false);
    try{await API.post("/pricing",pricing);showToast("✅ Pricing saved! Tenants will see updated prices.");}
    catch(e){showToast("⚠️ Locally saved, server sync failed: "+e.message);}
  };
  const saveHelpline=async()=>{
    localStorage.setItem("sa_helpline",JSON.stringify(helpline));
    try{await API.post("/helpline",helpline);showToast("✅ Helpline saved! It will update on all devices.");}
    catch(e){showToast("⚠️ Locally saved, server sync failed: "+e.message);}
  };

  const changeCredentials=async()=>{
    if(!credForm.currentPassword){showToast("❌ Please enter your current password","err");return;}
    if(credForm.newPassword&&credForm.newPassword!==credForm.confirmPassword){showToast("❌ New passwords do not match","err");return;}
    if(credForm.newPassword&&credForm.newPassword.length<6){showToast("❌ Password must be at least 6 characters","err");return;}
    if(credForm.newUsername&&credForm.newUsername.trim().length<3){showToast("❌ Username must be at least 3 characters","err");return;}
    setCredLoading(true);
    try{
      const res=await API.post("/superadmin/change-credentials",{
        currentPassword:credForm.currentPassword,
        newUsername:credForm.newUsername||undefined,
        newPassword:credForm.newPassword||undefined
      });
      if(res?.token){
        localStorage.setItem("gd_token",res.token);
      }
      showToast("✅ Credentials updated! Remember your new username/password.");
      setCredForm({currentPassword:"",newUsername:"",newPassword:"",confirmPassword:""});
    }catch(e){
      showToast("❌ "+e.message,"err");
    }
    setCredLoading(false);
  };
  const addOffer=async()=>{
    if(!offerForm.name||!offerForm.discount)return;
    const no=[...offers,{...offerForm,id:Date.now()}];
    setOffers(no);localStorage.setItem("sa_offers",JSON.stringify(no));
    setOfferForm({type:"percent"});
    try{ await API.post("/offers",no); showToast("✅ Offer added! Visible to distributors now."); }
    catch(e){ showToast("⚠️ Saved locally, server sync failed: "+e.message,"err"); }
  };
  const removeOffer=async(id)=>{
    const no=offers.filter(o=>o.id!==id);
    setOffers(no);localStorage.setItem("sa_offers",JSON.stringify(no));
    try{ await API.post("/offers",no); showToast("🗑️ Offer removed!"); }
    catch(e){ showToast("⚠️ Removed locally, server sync failed: "+e.message,"err"); }
  };

  // ── Computed ─────────────────────────────────────────────────
  const activePaying=tenants.filter(t=>t.status==="active"&&t.plan!=="trial").length;
  const mrr=PLANS.reduce((s,p)=>{
    const cnt=tenants.filter(t=>t.status==="active"&&t.plan===p.id).length;
    return s+cnt*(pricing[p.id]?.price||p.price);
  },0);
  const expiringSoon=tenants.filter(t=>{const d=daysLeft(t.trial_end);return t.status==="active"&&d<=7&&d>=0;});
  const alertList=tenants.filter(t=>{const d=daysLeft(t.trial_end);return(d<=3&&t.status==="active")||t.status==="expired"||t.status==="inactive";});

  // ── Growth Trend — new tenant signups per month, last 6 months ──
  const growthData=(()=>{
    const months=[];
    const now=new Date();
    for(let i=5;i>=0;i--){
      const d=new Date(now.getFullYear(),now.getMonth()-i,1);
      months.push({label:d.toLocaleDateString("en-IN",{month:"short"}),y:d.getFullYear(),m:d.getMonth()});
    }
    return months.map(({label,y,m})=>({
      label,
      value:tenants.filter(t=>{const c=new Date(t.created_at);return c.getFullYear()===y&&c.getMonth()===m;}).length
    }));
  })();

  // ── Churn — tenants that went inactive/expired ──
  const churnedTenants=tenants.filter(t=>t.status==="inactive"||t.status==="expired");

  // Trial requests (leads) from landing page
  const [trialRequests,setTrialRequests]=useState([]);
  const newReqCount=trialRequests.filter(r=>r.status==="new").length;
  useEffect(()=>{
    (async()=>{
      try{const d=await API.get("/trial-requests");setTrialRequests(Array.isArray(d)?d:[]);}catch(e){}
    })();
  },[]);
  const loadTrialRequests=async()=>{
    try{const d=await API.get("/trial-requests");setTrialRequests(Array.isArray(d)?d:[]);}catch(e){}
  };

  // Tenant status change log — used for churn tracking
  const [tenantStatusLog,setTenantStatusLog]=useState([]);
  useEffect(()=>{
    (async()=>{
      try{const d=await API.get("/tenants/status-log");setTenantStatusLog(Array.isArray(d)?d:[]);}catch(e){}
    })();
  },[]);
  const loadStatusLog=async()=>{
    try{const d=await API.get("/tenants/status-log");setTenantStatusLog(Array.isArray(d)?d:[]);}catch(e){}
  };

  // Monthly count of churned tenants — computed AFTER tenantStatusLog is declared above
  const churnThisMonth=(tenantStatusLog||[]).filter(l=>{
    const d=new Date(l.created_at);const n=new Date();
    return (l.new_status==="inactive"||l.new_status==="expired") && d.getMonth()===n.getMonth() && d.getFullYear()===n.getFullYear();
  }).length;

  // General activity log — every Super Admin action
  const [activityLog,setActivityLog]=useState([]);
  useEffect(()=>{
    (async()=>{
      try{const d=await API.get("/activity-log");setActivityLog(Array.isArray(d)?d:[]);}catch(e){}
    })();
  },[]);

  // ── Conversion Rate — how many leads/trials actually became paying ──
  const convertedLeads=trialRequests.filter(r=>r.status==="converted").length;
  const leadConversionRate=trialRequests.length ? Math.round((convertedLeads/trialRequests.length)*100) : 0;
  const trialToPayRate=tenants.length ? Math.round((activePaying/tenants.length)*100) : 0;
  const thisMonthPay=allPayments.filter(p=>{const d=new Date(p.paid_date||p.created_at);const n=new Date();return d.getMonth()===n.getMonth()&&d.getFullYear()===n.getFullYear();}).reduce((s,p)=>s+(+p.amount||0),0);

  // Filter + Sort
  const filtTenants=tenants
    .filter(t=>{
      if(srch&&!t.name?.toLowerCase().includes(srch.toLowerCase())&&!t.phone?.includes(srch)&&!t.owner_name?.toLowerCase().includes(srch.toLowerCase()))return false;
      if(filterStatus==="active")return t.status==="active"&&t.plan!=="trial";
      if(filterStatus==="trial")return t.plan==="trial"&&t.status==="active";
      if(filterStatus==="expired")return t.status==="expired"||t.status==="inactive";
      if(filterStatus==="expiring")return t.status==="active"&&daysLeft(t.trial_end)<=7;
      return true;
    })
    .sort((a,b)=>{
      if(sortBy==="expiry")return new Date(a.trial_end)-new Date(b.trial_end);
      if(sortBy==="name")return a.name?.localeCompare(b.name);
      if(sortBy==="plan")return a.plan?.localeCompare(b.plan);
      return new Date(b.created_at)-new Date(a.created_at);
    });

  // ── Render ───────────────────────────────────────────────────
  return(
    <div style={{minHeight:"100vh",background:C.bg,fontFamily:"system-ui,sans-serif",paddingBottom:70}}>

      {/* ── HEADER ── */}
      <div style={{background:C.side,padding:"0 14px",height:52,display:"flex",alignItems:"center",justifyContent:"space-between",position:"sticky",top:0,zIndex:100,boxShadow:"0 2px 8px rgba(0,0,0,0.2)"}}>
        <div style={{display:"flex",alignItems:"center",gap:8}}>
          <span style={{fontSize:20}}>👑</span>
          <div>
            <div style={{color:"#fff",fontWeight:800,fontSize:14}}>DawnToDusk Pro</div>
            <div style={{color:"#94A3B8",fontSize:9}}>Super Admin Panel</div>
          </div>
        </div>
        <div style={{display:"flex",gap:8,alignItems:"center"}}>
          {alertList.length>0&&<div style={{background:C.danger,color:"#fff",borderRadius:20,padding:"3px 8px",fontSize:11,fontWeight:700}}>🔔 {alertList.length}</div>}
          {helpline.phone&&<a href={`tel:${helpline.phone}`} style={{color:C.success,fontSize:11,fontWeight:700,textDecoration:"none",background:C.success+"20",padding:"4px 8px",borderRadius:6}}>📞</a>}
          <button onClick={()=>{if(window.confirm("Logout karna hai?"))onLogout();}} style={{background:"#EF4444",border:"none",color:"#fff",padding:"5px 12px",borderRadius:8,cursor:"pointer",fontSize:12,fontWeight:700}}>🚪 Logout</button>
        </div>
      </div>

      <div style={{padding:"12px 14px",maxWidth:700,margin:"0 auto"}}>

        {/* ── HOME TAB ── */}
        {saTab==="home"&&<>
          {/* Stats */}
          <div style={{display:"flex",gap:8,marginBottom:12}}>
            <StatCard icon="🏢" label="Total" value={tenants.length} color={C.primary}/>
            <StatCard icon="✅" label="Active" value={tenants.filter(t=>t.status==="active").length} color={C.success}/>
            <StatCard icon="⏳" label="Trial" value={tenants.filter(t=>t.plan==="trial"&&t.status==="active").length} color={C.warning}/>
            <StatCard icon="⏰" label="Expired" value={tenants.filter(t=>t.status==="expired"||t.status==="inactive").length} color={C.danger} onClick={()=>setSaTab("alerts")}/>
          </div>

          {/* Revenue Cards */}
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:12}}>
            <div style={{background:"linear-gradient(135deg,#1C2B3A,#2D4A63)",borderRadius:14,padding:14,color:"#fff"}}>
              <div style={{fontSize:11,color:"#94A3B8",marginBottom:4}}>Monthly Revenue</div>
              <div style={{fontSize:24,fontWeight:800,color:"#F59E0B"}}>₹{mrr.toLocaleString("en-IN")}</div>
              <div style={{fontSize:10,color:"#94A3B8",marginTop:2}}>MRR</div>
            </div>
            <div style={{background:"linear-gradient(135deg,#1C3A2B,#2D5A3F)",borderRadius:14,padding:14,color:"#fff"}}>
              <div style={{fontSize:11,color:"#94A3B8",marginBottom:4}}>This Month</div>
              <div style={{fontSize:24,fontWeight:800,color:C.success}}>₹{thisMonthPay.toLocaleString("en-IN")}</div>
              <div style={{fontSize:10,color:"#94A3B8",marginTop:2}}>Collected</div>
            </div>
          </div>

          {/* Alerts Banner */}
          {newReqCount>0&&<div onClick={()=>setSaTab("requests")} style={{background:"#FFFBEB",border:`1.5px solid ${C.warning}40`,borderRadius:12,padding:12,marginBottom:12,cursor:"pointer"}}>
            <div style={{fontWeight:700,color:C.warning,fontSize:13}}>📋 {newReqCount} New Trial Request{newReqCount>1?"s":""} — Tap to view!</div>
            <div style={{fontSize:11,color:C.sub,marginTop:3}}>{trialRequests.filter(r=>r.status==="new").slice(0,2).map(r=>r.name).join(", ")}{newReqCount>2?` +${newReqCount-2} more`:""}</div>
          </div>}

          {alertList.length>0&&<div onClick={()=>setSaTab("alerts")} style={{background:"#FEF2F2",border:`1.5px solid ${C.danger}40`,borderRadius:12,padding:12,marginBottom:12,cursor:"pointer"}}>
            <div style={{fontWeight:700,color:C.danger,fontSize:13}}>🔔 {alertList.length} Alert{alertList.length>1?"s":""} — Action Needed!</div>
            <div style={{fontSize:11,color:C.sub,marginTop:3}}>{alertList.slice(0,2).map(t=>t.name).join(", ")}{alertList.length>2?` +${alertList.length-2} more`:""}</div>
          </div>}

          {/* Expiring Soon */}
          {expiringSoon.length>0&&<div style={{background:"#fff",borderRadius:12,padding:14,marginBottom:12,border:`1px solid ${C.warning}30`}}>
            <div style={{fontWeight:700,fontSize:13,marginBottom:8,color:C.warning}}>⏰ Expiring This Week ({expiringSoon.length})</div>
            {expiringSoon.slice(0,3).map(t=>{
              const dl=daysLeft(t.trial_end);
              return <div key={t.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"6px 0",borderBottom:`1px solid ${C.border}`,fontSize:12}}>
                <div>
                  <span style={{fontWeight:700}}>{t.name}</span>
                  <span style={{color:C.sub,marginLeft:6,fontSize:11}}>{t.phone}</span>
                </div>
                <div style={{display:"flex",gap:6,alignItems:"center"}}>
                  <span style={{color:dl<=3?C.danger:C.warning,fontWeight:700,fontSize:11}}>{dl} days</span>
                  <button onClick={()=>openWA(t.phone,WA_TEMPLATES.trial(t,dl,helpline))} style={{background:"#25D366",border:"none",color:"#fff",padding:"3px 7px",borderRadius:5,cursor:"pointer",fontSize:10,fontWeight:700}}>📱</button>
                  <button onClick={()=>{setSelTenant(t);setPayForm({plan:"basic",months:1,method:"Cash",note:"",customAmt:""});setShowPayModal(true);}} style={{background:C.success,border:"none",color:"#fff",padding:"3px 7px",borderRadius:5,cursor:"pointer",fontSize:10,fontWeight:700}}>💰</button>
                </div>
              </div>;
            })}
          </div>}

          {/* Recent Payments */}
          <div style={{background:"#fff",borderRadius:12,padding:14,marginBottom:12}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
              <div style={{fontWeight:700,fontSize:13}}>💳 Recent Payments</div>
              <button onClick={()=>setSaTab("payments")} style={{background:"none",border:"none",color:C.primary,fontSize:11,cursor:"pointer",fontWeight:600}}>View All →</button>
            </div>
            {allPayments.slice(0,4).map(p=>(
              <div key={p.id} style={{display:"flex",justifyContent:"space-between",padding:"6px 0",borderBottom:`1px solid ${C.border}`,fontSize:12}}>
                <div>
                  <div style={{fontWeight:700}}>{p.tenant_name}</div>
                  <div style={{color:C.sub,fontSize:10}}>{p.invoice_no} • {p.plan} • {p.duration_months}mo</div>
                </div>
                <div style={{fontWeight:800,color:C.success}}>₹{fmt(p.amount)}</div>
              </div>
            ))}
            {allPayments.length===0&&<div style={{textAlign:"center",color:C.sub,fontSize:12,padding:8}}>No payments yet</div>}
          </div>

          {/* Quick Actions */}
          <div style={{background:"#fff",borderRadius:12,padding:14}}>
            <div style={{fontWeight:700,fontSize:13,marginBottom:10}}>⚡ Quick Actions</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
              {[
                {label:"➕ Add Distributor",color:C.success,action:()=>setSaTab("distributors")},
                {label:"💰 Record Payment",color:C.primary,action:()=>setSaTab("distributors")},
                {label:"📢 Send Announcement",color:C.purple,action:()=>setSaTab("announce")},
                {label:"💾 Download Backup",color:C.info,action:()=>setSaTab("settings")},
              ].map(a=>(
                <button key={a.label} onClick={a.action} style={{padding:"10px 8px",borderRadius:9,background:a.color+"15",border:`1.5px solid ${a.color}30`,color:a.color,cursor:"pointer",fontSize:12,fontWeight:700,fontFamily:"inherit",textAlign:"center"}}>
                  {a.label}
                </button>
              ))}
            </div>
          </div>
        </>}

        {/* ── DISTRIBUTORS TAB ── */}
        {saTab==="distributors"&&<>
          {/* Add Form */}
          <div style={{background:"#fff",borderRadius:14,padding:16,marginBottom:14,border:`1px solid ${C.border}`}}>
            <div style={{fontSize:14,fontWeight:700,marginBottom:12}}>➕ New Distributor ({pricing.trial_days||7} Days Trial)</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
              <Inp l="Firm Name *" v={saForm.name||""} oc={e=>setSaForm({...saForm,name:e.target.value})} p="Ram Distribution"/>
              <Inp l="Owner Name *" v={saForm.owner_name||""} oc={e=>setSaForm({...saForm,owner_name:e.target.value})} p="Ramesh Kumar"/>
              <Inp l="Phone *" v={saForm.phone||""} oc={e=>setSaForm({...saForm,phone:e.target.value})} p="9876543210"/>
              <Inp l="City" v={saForm.city||""} oc={e=>setSaForm({...saForm,city:e.target.value})} p="Mumbai"/>
              <Inp l="Username" v={saForm.username||""} oc={e=>setSaForm({...saForm,username:e.target.value})} p="ramesh123"/>
              <Inp l="Password" v={saForm.password||""} oc={e=>setSaForm({...saForm,password:e.target.value})} p="pass123"/>
            </div>
            {saMsg&&<div style={{padding:"10px 12px",borderRadius:8,marginTop:8,fontSize:12,background:saMsg.includes("✅")?"#F0FFF4":"#FEF2F2",color:saMsg.includes("✅")?C.success:C.danger,fontWeight:600,whiteSpace:"pre-line"}}>{saMsg}</div>}
            <button onClick={addTenant} disabled={loading} style={{marginTop:10,background:loading?"#ccc":C.success,border:"none",color:"#fff",padding:"12px",borderRadius:10,cursor:loading?"not-allowed":"pointer",fontSize:13,fontWeight:700,width:"100%",fontFamily:"inherit"}}>
              {loading?"⏳ Adding...":"✅ Add Distributor + Send Welcome WA"}
            </button>
          </div>

          {/* Search + Filter */}
          <div style={{display:"flex",gap:8,marginBottom:10,flexWrap:"wrap"}}>
            <input value={srch} onChange={e=>setSrch(e.target.value)} placeholder="🔍 Search..." style={{...IS,flex:2,minWidth:150}}/>
            <select value={filterStatus} onChange={e=>setFilterStatus(e.target.value)} style={{...IS,flex:1,minWidth:100}}>
              <option value="all">All ({tenants.length})</option>
              <option value="active">Active ({tenants.filter(t=>t.status==="active"&&t.plan!=="trial").length})</option>
              <option value="trial">Trial ({tenants.filter(t=>t.plan==="trial"&&t.status==="active").length})</option>
              <option value="expiring">Expiring ({expiringSoon.length})</option>
              <option value="expired">Expired ({tenants.filter(t=>t.status==="expired"||t.status==="inactive").length})</option>
            </select>
            <select value={sortBy} onChange={e=>setSortBy(e.target.value)} style={{...IS,flex:1,minWidth:100}}>
              <option value="expiry">By Expiry</option>
              <option value="name">By Name</option>
              <option value="plan">By Plan</option>
              <option value="recent">Recent</option>
            </select>
          </div>

          {/* Count */}
          <div style={{fontSize:12,color:C.sub,marginBottom:8}}>{filtTenants.length} distributors</div>

          {/* List */}
          {filtTenants.map(t=>{
            const dl=daysLeft(t.trial_end);
            const plan=PLANS.find(p=>p.id===t.plan);
            const isExpired=t.status==="expired"||t.status==="inactive"||(dl<0);
            const isExpiring=dl<=7&&dl>=0&&t.status==="active";
            const hist=payHistory[t.id];
            const stats=tenantStats[t.id];
            return(
              <div key={t.id} style={{background:"#fff",borderRadius:12,padding:14,marginBottom:10,border:`2px solid ${isExpired?C.danger+"40":isExpiring?C.warning+"40":C.success+"20"}`}}>
                {/* Top Row */}
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:8,marginBottom:8}}>
                  <div style={{flex:1}}>
                    <div style={{display:"flex",alignItems:"center",gap:6,flexWrap:"wrap",marginBottom:3}}>
                      <span style={{fontWeight:800,fontSize:14}}>{t.name}</span>
                      <span style={{fontSize:10,padding:"2px 7px",borderRadius:20,background:(plan?.color||C.primary)+"20",color:plan?.color||C.primary,fontWeight:700}}>{plan?.icon} {plan?.name||t.plan}</span>
                      <span style={{fontSize:10,padding:"2px 7px",borderRadius:20,background:t.status==="active"?C.success+"20":C.danger+"20",color:t.status==="active"?C.success:C.danger,fontWeight:700}}>{t.status}</span>
                    </div>
                    <div style={{fontSize:11,color:C.sub}}>👤 {t.owner_name} • 📞 {t.phone} • 🏙️ {t.city||"—"}</div>
                    {t.last_login&&<div style={{fontSize:10,color:C.sub}}>🕐 Last login: {new Date(t.last_login).toLocaleDateString("en-IN")}</div>}
                    <div style={{fontSize:11,marginTop:3}}>
                      <span style={{color:C.sub}}>Valid: {new Date(t.trial_end).toLocaleDateString("en-IN")}</span>
                      {t.status==="active"&&<span style={{marginLeft:6,fontWeight:700,color:dl>7?C.success:dl>3?C.warning:C.danger}}>
                        {dl>0?`(${dl} days left)`:"⚠️ Expires today!"}
                      </span>}
                      {isExpired&&dl<0&&<span style={{marginLeft:6,color:C.danger,fontWeight:700}}>⚠️ Expired {Math.abs(dl)} days ago</span>}
                    </div>
                    {/* Stats */}
                    {stats&&<div style={{display:"flex",gap:8,marginTop:4,fontSize:10,flexWrap:"wrap"}}>
                      <span style={{color:C.primary}}>📦 {stats.orders} orders</span>
                      <span style={{color:C.success}}>👥 {stats.customers} customers</span>
                      <span style={{color:C.warning}}>🧴 {stats.products} products</span>
                      {stats.lastLogin&&<span style={{color:C.sub}}>🕐 {new Date(stats.lastLogin).toLocaleDateString("en-IN")}</span>}
                    </div>}
                  </div>
                </div>

                {/* Action Buttons */}
                <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                  <button onClick={()=>{setSelTenant(t);setPayForm({plan:t.plan==="trial"?"basic":t.plan,months:1,method:"Cash",note:"",customAmt:""});setShowPayModal(true);}} style={{flex:2,padding:"8px 6px",borderRadius:8,background:C.success,border:"none",color:"#fff",cursor:"pointer",fontSize:12,fontWeight:700,fontFamily:"inherit"}}>
                    💰 Payment
                  </button>
                  <button onClick={()=>{setEditForm({...t});setShowEditModal(true);}} style={{flex:1,padding:"8px 6px",borderRadius:8,background:C.primary,border:"none",color:"#fff",cursor:"pointer",fontSize:12,fontWeight:700,fontFamily:"inherit"}}>
                    ✏️ Edit
                  </button>
                  <button onClick={()=>{setSelTenant(t);loadTenantStats(t.id);loadPayHistory(t.id);setShowProfileModal(true);}} style={{flex:1,padding:"8px 6px",borderRadius:8,background:C.info,border:"none",color:"#fff",cursor:"pointer",fontSize:12,fontWeight:700,fontFamily:"inherit"}}>
                    👁️ Profile
                  </button>
                  <button onClick={()=>toggleTenant(t)} style={{padding:"8px 10px",borderRadius:8,background:t.status==="active"?C.warning:C.success,border:"none",color:"#fff",cursor:"pointer",fontSize:12,fontWeight:700,fontFamily:"inherit"}}>
                    {t.status==="active"?"⏸":"▶"}
                  </button>
                  <button onClick={()=>{if(window.confirm(`Login as ${t.name}'s admin account for support? This is logged.`))impersonateTenant?.(t.id);}} style={{padding:"8px 10px",borderRadius:8,background:"#7C3AED",border:"none",color:"#fff",cursor:"pointer",fontSize:12,fontWeight:700}} title="Login as this distributor (support access)">
                    🔑
                  </button>
                  <button onClick={()=>{const msg=WA_TEMPLATES[t.plan==="trial"?"trial":"renewal"](t,dl,helpline);openWA(t.phone,msg);}} style={{padding:"8px 10px",borderRadius:8,background:"#25D366",border:"none",color:"#fff",cursor:"pointer",fontSize:12,fontWeight:700}}>
                    📱
                  </button>
                  <button onClick={()=>deleteTenant(t.id)} style={{padding:"8px 10px",borderRadius:8,background:C.danger,border:"none",color:"#fff",cursor:"pointer",fontSize:12,fontWeight:700}}>
                    🗑️
                  </button>
                </div>

                {/* Trial Extension */}
                {t.plan==="trial"&&t.status==="active"&&<div style={{display:"flex",gap:6,marginTop:8,alignItems:"center"}}>
                  <span style={{fontSize:11,color:C.sub}}>Extend Trial:</span>
                  {[3,7,15,30].map(d=>(
                    <button key={d} onClick={()=>extendTrial(t,d)} style={{padding:"4px 10px",borderRadius:7,background:C.bg,border:`1px solid ${C.border}`,cursor:"pointer",fontSize:10,fontWeight:600,fontFamily:"inherit"}}>+{d}d</button>
                  ))}
                </div>}

                {/* Last Payment */}
                {hist&&hist.length>0&&<div style={{marginTop:8,background:C.bg,borderRadius:7,padding:"6px 10px",fontSize:11}}>
                  <span style={{color:C.sub}}>Last Payment: </span>
                  <span style={{fontWeight:700,color:C.success}}>₹{fmt(hist[0].amount)}</span>
                  <span style={{color:C.sub}}> • {hist[0].plan} • {new Date(hist[0].paid_date).toLocaleDateString("en-IN")}</span>
                  <span style={{color:C.primary,marginLeft:6}}>({hist.length} total)</span>
                </div>}
              </div>
            );
          })}
          {filtTenants.length===0&&<div style={{textAlign:"center",padding:32,color:C.sub,background:"#fff",borderRadius:12}}>No distributors found!</div>}
        </>}

        {/* ── REVENUE TAB ── */}
        {saTab==="revenue"&&<div>
          <div style={{fontSize:14,fontWeight:800,marginBottom:12,color:C.success}}>💰 Revenue Dashboard</div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:14}}>
            {[
              {l:"MRR",v:`₹${mrr.toLocaleString("en-IN")}`,c:C.success,sub:"Monthly Recurring"},
              {l:"ARR",v:`₹${(mrr*12).toLocaleString("en-IN")}`,c:C.primary,sub:"Annual Run Rate"},
              {l:"This Month",v:`₹${thisMonthPay.toLocaleString("en-IN")}`,c:C.warning,sub:"Collected"},
              {l:"Paying",v:activePaying,c:C.info,sub:"Active customers"},
            ].map(s=>(
              <div key={s.l} style={{background:"#fff",borderRadius:12,padding:14,border:`2px solid ${s.c}20`}}>
                <div style={{fontSize:11,color:C.sub}}>{s.sub}</div>
                <div style={{fontSize:22,fontWeight:800,color:s.c,marginTop:4}}>{s.v}</div>
                <div style={{fontSize:10,color:C.sub}}>{s.l}</div>
              </div>
            ))}
          </div>

          {/* Growth Trend */}
          <div style={{background:"#fff",borderRadius:12,padding:14,marginBottom:14,border:`1px solid ${C.border}`}}>
            <div style={{fontSize:12,fontWeight:700,marginBottom:4}}>📈 New Signups — Last 6 Months</div>
            <MonthBarChart data={growthData} color={C.primary}/>
          </div>

          {/* Conversion Rates */}
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:14}}>
            <div style={{background:"#fff",borderRadius:12,padding:14,border:`2px solid ${C.success}20`}}>
              <div style={{fontSize:11,color:C.sub}}>Lead → Paid</div>
              <div style={{fontSize:22,fontWeight:800,color:C.success,marginTop:4}}>{leadConversionRate}%</div>
              <div style={{fontSize:10,color:C.sub}}>{convertedLeads} of {trialRequests.length} leads converted</div>
            </div>
            <div style={{background:"#fff",borderRadius:12,padding:14,border:`2px solid ${C.info}20`}}>
              <div style={{fontSize:11,color:C.sub}}>Trial → Paid</div>
              <div style={{fontSize:22,fontWeight:800,color:C.info,marginTop:4}}>{trialToPayRate}%</div>
              <div style={{fontSize:10,color:C.sub}}>{activePaying} of {tenants.length} tenants paying</div>
            </div>
          </div>

          {/* Churn */}
          <div style={{background:"#fff",borderRadius:12,padding:14,marginBottom:14,border:`1px solid ${C.border}`}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
              <div style={{fontSize:12,fontWeight:700}}>📉 Churn</div>
              <div style={{fontSize:11,color:churnThisMonth>0?C.danger:C.sub,fontWeight:700}}>{churnThisMonth} this month</div>
            </div>
            {churnedTenants.length===0&&<div style={{fontSize:11,color:C.sub,textAlign:"center",padding:12}}>No churned distributors yet 🎉</div>}
            {churnedTenants.length>0&&<div style={{maxHeight:200,overflowY:"auto"}}>
              {churnedTenants.slice(0,10).map(t=>{
                const log=tenantStatusLog.find(l=>l.tenant_id===t.id);
                return <div key={t.id} style={{padding:"6px 0",borderBottom:`1px solid ${C.border}`,fontSize:11}}>
                  <span style={{fontWeight:700}}>{t.name}</span>
                  <span style={{color:t.status==="expired"?C.warning:C.danger,marginLeft:6}}>{t.status==="expired"?"⏰ Expired":"⏸ Paused"}</span>
                  {log?.reason&&<div style={{color:C.sub,fontSize:10,marginTop:2}}>💬 {log.reason}</div>}
                </div>;
              })}
            </div>}
          </div>

          {/* Plan wise breakdown */}
          <div style={{background:"#fff",borderRadius:12,padding:14,marginBottom:12}}>
            <div style={{fontWeight:700,marginBottom:10}}>📊 Plan Wise Breakdown</div>
            {PLANS.map(p=>{
              const cnt=tenants.filter(t=>t.status==="active"&&t.plan===p.id).length;
              const rev=cnt*(pricing[p.id]?.price||p.price);
              return <div key={p.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"8px 0",borderBottom:`1px solid ${C.border}`}}>
                <div style={{display:"flex",alignItems:"center",gap:8}}>
                  <span style={{fontSize:16}}>{p.icon}</span>
                  <div>
                    <div style={{fontWeight:700,fontSize:13,color:p.color}}>{p.name}</div>
                    <div style={{fontSize:11,color:C.sub}}>₹{pricing[p.id]?.price||p.price}/mo × {cnt} customers</div>
                  </div>
                </div>
                <div style={{fontWeight:800,color:p.color}}>₹{rev.toLocaleString("en-IN")}</div>
              </div>;
            })}
          </div>

          {/* Projection */}
          <div style={{background:"#fff",borderRadius:12,padding:14,marginBottom:12}}>
            <div style={{fontWeight:700,marginBottom:10}}>📈 Revenue Projection</div>
            <div style={{display:"flex",gap:6,marginBottom:10,flexWrap:"wrap"}}>
              {PLANS.map(p=><button key={p.id} onClick={()=>setPricing({...pricing,_projPlan:p.id})} style={{padding:"4px 10px",borderRadius:7,border:`1.5px solid ${(pricing._projPlan||"basic")===p.id?p.color:C.border}`,background:(pricing._projPlan||"basic")===p.id?p.color+"15":"#fff",color:(pricing._projPlan||"basic")===p.id?p.color:C.text,cursor:"pointer",fontSize:11,fontWeight:700,fontFamily:"inherit"}}>{p.icon} {p.name}</button>)}
            </div>
            {(()=>{const pid=pricing._projPlan||"basic";const pp=pricing[pid]?.price||PLANS.find(p=>p.id===pid)?.price||499;
            return [10,25,50,100,500].map(n=>(
              <div key={n} style={{display:"flex",justifyContent:"space-between",padding:"6px 0",fontSize:12,borderBottom:`1px solid ${C.border}`}}>
                <span style={{color:C.sub}}>{n} customers</span>
                <div style={{textAlign:"right"}}>
                  <div style={{fontWeight:700,color:C.success}}>₹{(n*pp).toLocaleString("en-IN")}/mo</div>
                  <div style={{fontSize:10,color:C.sub}}>₹{(n*pp*12).toLocaleString("en-IN")}/year</div>
                </div>
              </div>
            ))})()}
          </div>

          {/* Expiring this week */}
          <div style={{background:"#fff",borderRadius:12,padding:14}}>
            <div style={{fontWeight:700,marginBottom:10,color:C.warning}}>⏰ Expiring This Week ({expiringSoon.length})</div>
            {expiringSoon.length===0?<div style={{color:C.sub,fontSize:12,textAlign:"center",padding:8}}>None ✅</div>:
            expiringSoon.map(t=>{
              const dl=daysLeft(t.trial_end);
              return <div key={t.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"8px 0",borderBottom:`1px solid ${C.border}`}}>
                <div>
                  <div style={{fontWeight:700,fontSize:12}}>{t.name}</div>
                  <div style={{fontSize:11,color:C.sub}}>{t.phone}</div>
                </div>
                <div style={{display:"flex",gap:6,alignItems:"center"}}>
                  <span style={{color:dl<=3?C.danger:C.warning,fontWeight:700,fontSize:11}}>{dl} days</span>
                  <button onClick={()=>openWA(t.phone,WA_TEMPLATES.trial(t,dl,helpline))} style={{background:"#25D366",border:"none",color:"#fff",padding:"4px 8px",borderRadius:6,cursor:"pointer",fontSize:10,fontWeight:700}}>📱 WA</button>
                  <button onClick={()=>{setSelTenant(t);setPayForm({plan:"basic",months:1,method:"Cash",note:"",customAmt:""});setShowPayModal(true);}} style={{background:C.success,border:"none",color:"#fff",padding:"4px 8px",borderRadius:6,cursor:"pointer",fontSize:10,fontWeight:700}}>💰 Pay</button>
                </div>
              </div>;
            })}
          </div>
        </div>}

        {/* ── PAYMENTS TAB ── */}
        {saTab==="payments"&&<div>
          <div style={{fontSize:14,fontWeight:800,marginBottom:12}}>💳 All Payments</div>
          {/* Search + Filter */}
          <div style={{display:"flex",gap:8,marginBottom:10,flexWrap:"wrap"}}>
            <input placeholder="🔍 Distributor search..." onChange={e=>setSrch(e.target.value)} style={{flex:2,padding:"9px 12px",borderRadius:9,border:`1.5px solid ${C.border}`,fontSize:12,fontFamily:"inherit",outline:"none",background:"#F8FAFC",minWidth:150}}/>
            <select onChange={e=>{const v=e.target.value;setSrch(v==="all"?"":v);}} style={{flex:1,padding:"9px",borderRadius:9,border:`1.5px solid ${C.border}`,fontSize:12,fontFamily:"inherit",background:"#F8FAFC",minWidth:100}}>
              <option value="all">All Plans</option>
              {PLANS.map(p=><option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <div style={{background:"linear-gradient(135deg,#1C2B3A,#2D4A63)",borderRadius:12,padding:14,marginBottom:12,color:"#fff"}}>
            <div style={{fontSize:11,color:"#94A3B8"}}>Total Collected</div>
            <div style={{fontSize:28,fontWeight:800,color:"#F59E0B"}}>₹{allPayments.reduce((s,p)=>s+(+p.amount||0),0).toLocaleString("en-IN")}</div>
            <div style={{fontSize:11,color:"#94A3B8",marginTop:2}}>{allPayments.length} transactions</div>
          </div>
          <button onClick={()=>{
            const csv=["Invoice,Distributor,Phone,Amount,Plan,Duration,Method,Paid Date,Expiry",...allPayments.map(p=>`${p.invoice_no},"${p.tenant_name}",${p.tenant_phone||""},${p.amount},${p.plan},${p.duration_months}mo,${p.method},${p.paid_date},${p.expiry_date}`)].join("\n");
            const blob=new Blob(["\uFEFF"+csv],{type:"text/csv"});const url=URL.createObjectURL(blob);const a=document.createElement("a");a.href=url;a.download=`payments_${todayStr()}.csv`;a.click();
            showToast("✅ CSV downloaded!");
          }} style={{width:"100%",padding:"9px",background:C.success,border:"none",color:"#fff",borderRadius:9,cursor:"pointer",fontSize:12,fontWeight:700,fontFamily:"inherit",marginBottom:12}}>⬇️ Download CSV</button>
          {allPayments.map(p=>(
            <div key={p.id} style={{background:"#fff",borderRadius:10,padding:12,marginBottom:8,border:`1px solid ${C.border}`}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
                <div>
                  <div style={{fontWeight:700,fontSize:13}}>{p.tenant_name}</div>
                  <div style={{fontSize:11,color:C.sub}}>{p.invoice_no} • {new Date(p.paid_date).toLocaleDateString("en-IN")}</div>
                  <div style={{fontSize:11,color:C.sub}}>{p.plan} • {p.duration_months}mo • {p.method}</div>
                  <div style={{fontSize:10,color:C.sub}}>Valid till: {new Date(p.expiry_date).toLocaleDateString("en-IN")}</div>
                </div>
                <div style={{textAlign:"right"}}>
                  <div style={{fontWeight:800,fontSize:16,color:C.success}}>₹{fmt(p.amount)}</div>
                  <button onClick={()=>{
                    const t=tenants.find(x=>x.id===p.tenant_id)||{name:p.tenant_name,phone:p.tenant_phone||""};
                    printSAInvoice(t,p.invoice_no,p.amount,p.plan,p.duration_months,p.expiry_date,p.method);
                  }} style={{marginTop:5,padding:"4px 10px",background:"#1C2B3A",color:"#fff",border:"none",borderRadius:6,cursor:"pointer",fontSize:10,fontWeight:700}}>
                    🖨️ Print
                  </button>
                </div>
              </div>
            </div>
          ))}
          {allPayments.length===0&&<div style={{textAlign:"center",padding:32,color:C.sub,background:"#fff",borderRadius:12}}>No payments yet</div>}
        </div>}

        {/* ── ALERTS TAB ── */}
        {saTab==="alerts"&&<div>
          <div style={{fontSize:14,fontWeight:800,marginBottom:12,color:C.danger}}>🔔 Smart Alerts ({alertList.length})</div>
          {alertList.length===0?<div style={{textAlign:"center",padding:40,background:"#fff",borderRadius:12}}>
            <div style={{fontSize:40,marginBottom:8}}>✅</div>
            <div style={{fontWeight:700,color:C.success}}>All good!</div>
          </div>:
          alertList.map(t=>{
            const dl=daysLeft(t.trial_end);
            const alertType=t.status==="expired"?"Expired":t.status==="inactive"?"Inactive":`${dl} days left`;
            return <div key={t.id} style={{background:"#fff",borderRadius:10,padding:12,marginBottom:8,border:`2px solid ${C.danger}30`}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:8}}>
                <div>
                  <div style={{fontWeight:700,fontSize:13}}>{t.name}</div>
                  <div style={{fontSize:11,color:C.sub}}>{t.phone} • {t.city||"—"}</div>
                  <div style={{fontSize:12,color:C.danger,fontWeight:700,marginTop:3}}>⚠️ {alertType}</div>
                </div>
                <span style={{fontSize:10,padding:"3px 8px",borderRadius:20,background:C.danger+"20",color:C.danger,fontWeight:700}}>{t.plan}</span>
              </div>
              <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                <button onClick={()=>{setSelTenant(t);setPayForm({plan:"basic",months:1,method:"Cash",note:"",customAmt:""});setShowPayModal(true);}} style={{flex:2,padding:"7px",borderRadius:7,background:C.success,border:"none",color:"#fff",cursor:"pointer",fontSize:11,fontWeight:700,fontFamily:"inherit"}}>💰 Payment</button>
                <button onClick={()=>toggleTenant(t)} style={{flex:1,padding:"7px",borderRadius:7,background:C.warning,border:"none",color:"#fff",cursor:"pointer",fontSize:11,fontWeight:700,fontFamily:"inherit"}}>▶ Activate</button>
                <button onClick={()=>openWA(t.phone,WA_TEMPLATES[t.status==="expired"?"expired":"trial"](t,dl,helpline))} style={{padding:"7px 12px",borderRadius:7,background:"#25D366",border:"none",color:"#fff",cursor:"pointer",fontSize:11,fontWeight:700}}>📱</button>
                <button onClick={()=>{setEditForm({...t});setShowEditModal(true);}} style={{padding:"7px 12px",borderRadius:7,background:C.primary,border:"none",color:"#fff",cursor:"pointer",fontSize:11,fontWeight:700}}>✏️</button>
              </div>
            </div>;
          })}
        </div>}

        {/* ── REQUESTS (LEADS) TAB ── */}
        {saTab==="requests"&&<div>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
            <div style={{fontSize:14,fontWeight:800}}>📋 Trial Requests {newReqCount>0&&<span style={{background:C.danger,color:"#fff",borderRadius:20,padding:"2px 8px",fontSize:11,marginLeft:6}}>{newReqCount} new</span>}</div>
            <button onClick={loadTrialRequests} style={{background:"none",border:"none",cursor:"pointer",fontSize:18}}>🔄</button>
          </div>

          {trialRequests.length===0&&<div style={{textAlign:"center",padding:30,color:C.sub}}>No trial requests yet.<br/>Requests from your landing page will appear here.</div>}

          {trialRequests.map(r=>{
            const phone=(r.phone||"").replace(/\D/g,"");
            const statusColor={new:C.danger,contacted:C.warning,converted:C.success,rejected:C.sub}[r.status]||C.sub;
            const statusLabel={new:"🆕 New",contacted:"📞 Contacted",converted:"✅ Converted",rejected:"❌ Rejected"}[r.status]||r.status;
            const waMsg=encodeURIComponent(`Hi ${r.name}! Thanks for your interest in DawnToDusk Pro. I'm here to help you get started with your free 7-day trial. When would be a good time to talk?`);
            return <div key={r.id} style={{background:"#fff",borderRadius:12,padding:14,marginBottom:10,border:`1.5px solid ${statusColor}30`,borderLeft:`4px solid ${statusColor}`}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:6}}>
                <div>
                  <div style={{fontWeight:800,fontSize:14}}>{r.name}</div>
                  {r.business_name&&<div style={{fontSize:12,color:C.sub}}>{r.business_name}</div>}
                </div>
                <span style={{fontSize:10,fontWeight:700,color:statusColor,background:statusColor+"15",padding:"3px 8px",borderRadius:10}}>{statusLabel}</span>
              </div>
              <div style={{fontSize:12,color:C.text,marginBottom:3}}>📞 {r.phone}{r.city?` • 📍 ${r.city}`:""}</div>
              {r.plan&&<div style={{fontSize:11,color:C.sub,marginBottom:3}}>Interested in: <b>{r.plan}</b> plan</div>}
              {r.message&&<div style={{fontSize:12,color:C.text,background:C.bg,padding:8,borderRadius:8,marginTop:6,marginBottom:6}}>"{r.message}"</div>}
              <div style={{fontSize:10,color:C.sub,marginBottom:8}}>{new Date(r.created_at).toLocaleString("en-IN",{timeZone:"Asia/Kolkata"})}</div>
              <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                {phone&&<a href={`https://wa.me/91${phone}?text=${waMsg}`} target="_blank" rel="noreferrer" style={{textDecoration:"none"}}>
                  <button style={{padding:"7px 14px",background:"#25D366",border:"none",color:"#fff",borderRadius:8,cursor:"pointer",fontSize:12,fontWeight:700,fontFamily:"inherit"}}>📱 WhatsApp</button>
                </a>}
                {phone&&<a href={`tel:${phone}`} style={{textDecoration:"none"}}>
                  <button style={{padding:"7px 14px",background:C.info,border:"none",color:"#fff",borderRadius:8,cursor:"pointer",fontSize:12,fontWeight:700,fontFamily:"inherit"}}>📞 Call</button>
                </a>}
                {r.status==="new"&&<button onClick={async()=>{try{await API.put(`/trial-requests/${r.id}`,{status:"contacted"});loadTrialRequests();}catch(e){}}} style={{padding:"7px 14px",background:C.warning,border:"none",color:"#fff",borderRadius:8,cursor:"pointer",fontSize:12,fontWeight:700,fontFamily:"inherit"}}>📞 Mark Contacted</button>}
                {r.status!=="converted"&&<button onClick={async()=>{try{await API.put(`/trial-requests/${r.id}`,{status:"converted"});loadTrialRequests();}catch(e){}}} style={{padding:"7px 14px",background:C.success,border:"none",color:"#fff",borderRadius:8,cursor:"pointer",fontSize:12,fontWeight:700,fontFamily:"inherit"}}>✅ Converted</button>}
                <button onClick={async()=>{if(!window.confirm("Delete this request?"))return;try{await API.del(`/trial-requests/${r.id}`);loadTrialRequests();}catch(e){}}} style={{padding:"7px 14px",background:"none",border:`1px solid ${C.border}`,color:C.sub,borderRadius:8,cursor:"pointer",fontSize:12,fontFamily:"inherit"}}>🗑️</button>
              </div>
            </div>;
          })}
        </div>}

        {/* ── SETTINGS TAB ── */}
        {saTab==="settings"&&<div>
          <div style={{fontSize:14,fontWeight:800,marginBottom:12}}>⚙️ Settings</div>

          {/* Pricing */}
          <div style={{background:"#fff",borderRadius:12,padding:14,marginBottom:12,border:`1px solid ${C.border}`}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
              <div style={{fontWeight:700}}>💲 Pricing</div>
              <button onClick={()=>editPrice?savePricing():setEditPrice(true)} style={{padding:"5px 12px",borderRadius:7,background:editPrice?C.success:C.primary,border:"none",color:"#fff",cursor:"pointer",fontSize:11,fontWeight:700,fontFamily:"inherit"}}>
                {editPrice?"💾 Save":"✏️ Edit"}
              </button>
            </div>
            <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:10}}>
              <span style={{fontSize:12,color:C.sub}}>Trial Days:</span>
              {editPrice
                ?<input type="number" value={pricing.trial_days||7} onChange={e=>setPricing({...pricing,trial_days:+e.target.value})} style={{width:60,padding:"5px 8px",borderRadius:7,border:`1.5px solid ${C.primary}`,fontSize:13,textAlign:"center",fontFamily:"inherit"}}/>
                :<span style={{fontWeight:800,fontSize:16,color:C.primary}}>{pricing.trial_days||7} Days FREE</span>}
            </div>
            {PLANS.map(p=>(
              <div key={p.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"8px 0",borderBottom:`1px solid ${C.border}`}}>
                <span style={{fontWeight:700,color:p.color}}>{p.icon} {p.name}</span>
                {editPrice
                  ?<div style={{display:"flex",alignItems:"center",gap:4}}>
                    <span>₹</span>
                    <input type="number" value={pricing[p.id]?.price||p.price} onChange={e=>setPricing({...pricing,[p.id]:{...pricing[p.id],price:+e.target.value}})} style={{width:70,padding:"4px 6px",borderRadius:6,border:`1.5px solid ${p.color}`,fontSize:13,fontWeight:700,textAlign:"center",fontFamily:"inherit"}}/>
                    <span style={{fontSize:11,color:C.sub}}>/mo</span>
                  </div>
                  :<span style={{fontWeight:800,color:p.color}}>₹{(pricing[p.id]?.price||p.price).toLocaleString("en-IN")}/mo</span>}
              </div>
            ))}
          </div>

          {/* Helpline */}
          <div style={{background:"#fff",borderRadius:12,padding:14,marginBottom:12,border:`1px solid ${C.border}`}}>
            <div style={{fontWeight:700,marginBottom:10}}>📞 Helpline</div>
            <Inp l="Phone *" v={helpline.phone||""} oc={e=>setHelpline({...helpline,phone:e.target.value})} p="6284393851"/>
            <Inp l="WhatsApp" v={helpline.whatsapp||""} oc={e=>setHelpline({...helpline,whatsapp:e.target.value})} p="6284393851"/>
            <Inp l="Email" v={helpline.email||""} oc={e=>setHelpline({...helpline,email:e.target.value})} p="support@dawntodusk.in"/>
            <Inp l="Hours" v={helpline.hours||""} oc={e=>setHelpline({...helpline,hours:e.target.value})} p="Mon-Sat 10AM-6PM"/>
            <button onClick={saveHelpline} style={{width:"100%",padding:"10px",background:C.success,border:"none",color:"#fff",borderRadius:9,cursor:"pointer",fontSize:13,fontWeight:700,fontFamily:"inherit"}}>💾 Save Helpline</button>
          </div>

          {/* Our GST Business Details — used on subscription invoices sent to distributors */}
          <div style={{background:"#fff",borderRadius:12,padding:14,marginBottom:12,border:`1px solid ${C.border}`}}>
            <div style={{fontWeight:700,marginBottom:4}}>🧾 Our GST Details</div>
            <div style={{fontSize:10,color:C.sub,marginBottom:10}}>Used on the subscription invoices distributors receive when they pay you.</div>
            <Inp l="Business Name" v={helpline.our_business_name||""} oc={e=>setHelpline({...helpline,our_business_name:e.target.value})} p="DawnToDusk Pro"/>
            <Inp l="Our GSTIN" v={helpline.our_gstin||""} oc={e=>setHelpline({...helpline,our_gstin:e.target.value})} p="27XXXXX0000X1Z5"/>
            <Inp l="Registered State" v={helpline.our_state||""} oc={e=>setHelpline({...helpline,our_state:e.target.value})} p="Maharashtra"/>
            <Inp l="Business Address" v={helpline.our_address||""} oc={e=>setHelpline({...helpline,our_address:e.target.value})} p="Office address..."/>
            <button onClick={saveHelpline} style={{width:"100%",padding:"10px",background:C.success,border:"none",color:"#fff",borderRadius:9,cursor:"pointer",fontSize:13,fontWeight:700,fontFamily:"inherit"}}>💾 Save GST Details</button>
          </div>

          {/* Activity Log */}
          <div style={{background:"#fff",borderRadius:12,padding:14,marginBottom:12,border:`1px solid ${C.border}`}}>
            <div style={{fontWeight:700,marginBottom:10}}>📋 Activity Log</div>
            {activityLog.length===0&&<div style={{fontSize:11,color:C.sub,textAlign:"center",padding:12}}>No activity recorded yet</div>}
            {activityLog.length>0&&<div style={{maxHeight:280,overflowY:"auto"}}>
              {activityLog.slice(0,50).map(a=>(
                <div key={a.id} style={{padding:"7px 0",borderBottom:`1px solid ${C.border}`,fontSize:11}}>
                  <div style={{display:"flex",justifyContent:"space-between"}}>
                    <span style={{fontWeight:700,color:C.primary}}>{a.action}</span>
                    <span style={{color:C.sub,fontSize:10}}>{new Date(a.created_at).toLocaleString("en-IN",{timeZone:"Asia/Kolkata",day:"2-digit",month:"short",hour:"2-digit",minute:"2-digit"})}</span>
                  </div>
                  <div style={{color:C.sub,marginTop:2}}>{a.details}{a.tenant_name?` — ${a.tenant_name}`:""}</div>
                </div>
              ))}
            </div>}
          </div>

          {/* Super Admin Credentials */}
          <div style={{background:"#fff",borderRadius:12,padding:14,marginBottom:12,border:`1px solid ${C.border}`}}>
            <div style={{fontWeight:700,marginBottom:4}}>🔐 Super Admin Login Update</div>
            <div style={{fontSize:11,color:C.sub,marginBottom:10}}>Change your login credentials for security. Current password is always required.</div>

            <Inp l="Current Password *" v={credForm.currentPassword} t={credShow?"text":"password"}
              oc={e=>setCredForm({...credForm,currentPassword:e.target.value})} p="Abhi ka password"/>

            <Inp l="New Username (optional)" v={credForm.newUsername}
              oc={e=>setCredForm({...credForm,newUsername:e.target.value})} p="Leave blank to keep unchanged"/>

            <Inp l="New Password (optional)" v={credForm.newPassword} t={credShow?"text":"password"}
              oc={e=>setCredForm({...credForm,newPassword:e.target.value})} p="Min 6 characters"/>

            {credForm.newPassword&&<Inp l="Confirm New Password *" v={credForm.confirmPassword} t={credShow?"text":"password"}
              oc={e=>setCredForm({...credForm,confirmPassword:e.target.value})} p="Re-type new password"/>}

            <label style={{display:"flex",alignItems:"center",gap:6,fontSize:11,color:C.sub,marginBottom:10,cursor:"pointer"}}>
              <input type="checkbox" checked={credShow} onChange={e=>setCredShow(e.target.checked)}/>
              Show Password
            </label>

            <button onClick={changeCredentials} disabled={credLoading}
              style={{width:"100%",padding:"10px",background:credLoading?C.sub:C.danger,border:"none",color:"#fff",borderRadius:9,cursor:credLoading?"not-allowed":"pointer",fontSize:13,fontWeight:700,fontFamily:"inherit"}}>
              {credLoading?"⏳ Updating...":"🔒 Update Login Details"}
            </button>
            <div style={{fontSize:10,color:C.sub,marginTop:6,textAlign:"center"}}>⚠️ Remember your new credentials after update — old ones will stop working</div>
          </div>

          {/* Offers */}
          <div style={{background:"#fff",borderRadius:12,padding:14,marginBottom:12,border:`1px solid ${C.border}`}}>
            <div style={{fontWeight:700,marginBottom:10}}>🎁 Offers & Coupons</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:8}}>
              <Inp l="Offer Name *" v={offerForm.name||""} oc={e=>setOfferForm({...offerForm,name:e.target.value})} p="Diwali Sale"/>
              <div>
                <div style={{fontSize:11,fontWeight:600,color:C.sub,marginBottom:4}}>Type</div>
                <select value={offerForm.type||"percent"} onChange={e=>setOfferForm({...offerForm,type:e.target.value})} style={{...IS}}>
                  <option value="percent">% Off</option>
                  <option value="fixed">₹ Off</option>
                  <option value="days">+ Days</option>
                </select>
              </div>
              <Inp l="Value *" v={offerForm.discount||""} oc={e=>setOfferForm({...offerForm,discount:e.target.value})} p="20"/>
              <Inp l="Coupon Code" v={offerForm.code||""} oc={e=>setOfferForm({...offerForm,code:e.target.value.toUpperCase()})} p="SAVE20"/>
              <Inp l="From" v={offerForm.from||""} oc={e=>setOfferForm({...offerForm,from:e.target.value})} t="date"/>
              <Inp l="Till" v={offerForm.till||""} oc={e=>setOfferForm({...offerForm,till:e.target.value})} t="date"/>
            </div>
            <button onClick={addOffer} style={{width:"100%",padding:"9px",background:C.warning,border:"none",color:"#fff",borderRadius:9,cursor:"pointer",fontSize:12,fontWeight:700,fontFamily:"inherit",marginBottom:8}}>🎁 Add Offer</button>
            {offers.map(o=>{
              const isValid=(!o.from||o.from<=todayStr())&&(!o.till||o.till>=todayStr());
              return <div key={o.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"8px 0",borderBottom:`1px solid ${C.border}`,fontSize:12}}>
                <div>
                  <span style={{fontWeight:700}}>{o.name}</span>
                  <span style={{color:C.warning,marginLeft:6}}>{o.type==="percent"?`${o.discount}% off`:o.type==="fixed"?`₹${o.discount} off`:`+${o.discount} days`}</span>
                  {o.code&&<span style={{color:C.primary,marginLeft:6,fontSize:10}}>🏷️{o.code}</span>}
                  <span style={{marginLeft:6,fontSize:10,color:isValid?C.success:C.danger}}>{isValid?"✅":"⏰"}</span>
                </div>
                <button onClick={()=>removeOffer(o.id)} style={{background:"none",border:`1px solid ${C.danger}`,color:C.danger,padding:"3px 7px",borderRadius:5,cursor:"pointer",fontSize:11}}>🗑️</button>
              </div>;
            })}
          </div>

          {/* WA Templates */}
          <div style={{background:"#fff",borderRadius:12,padding:14,marginBottom:12,border:`1px solid ${C.border}`}}>
            <div style={{fontWeight:700,marginBottom:8}}>📱 WhatsApp Templates</div>
            <div style={{display:"flex",flexDirection:"column",gap:6}}>
              {[
                {label:"Welcome Message",key:"welcome",sample:{name:"Ram Trading",owner_name:"Ramesh",phone:"9876543210",username:"ramesh123"}},
                {label:"Trial Reminder",key:"trial",sample:{name:"Ram Trading",owner_name:"Ramesh",phone:"9876543210"}},
                {label:"Expired Notice",key:"expired",sample:{name:"Ram Trading",owner_name:"Ramesh",phone:"9876543210"}},
              ].map(t=>(
                <button key={t.key} onClick={()=>{const msg=t.key==="welcome"?WA_TEMPLATES.welcome(t.sample,helpline):t.key==="trial"?WA_TEMPLATES.trial(t.sample,3,helpline):WA_TEMPLATES.expired(t.sample,helpline);alert("Preview:\n\n"+msg);}} style={{padding:"9px 12px",borderRadius:8,background:C.bg,border:`1px solid ${C.border}`,cursor:"pointer",fontSize:12,fontWeight:600,fontFamily:"inherit",textAlign:"left"}}>
                  👁️ Preview: {t.label}
                </button>
              ))}
            </div>
          </div>

          {/* Backup */}
          <div style={{background:"#fff",borderRadius:12,padding:14,border:`1px solid ${C.border}`}}>
            <div style={{fontWeight:700,marginBottom:10}}>💾 Backup</div>
            <div style={{display:"flex",flexDirection:"column",gap:8}}>
              <button onClick={()=>{const data={exported:new Date().toISOString(),tenants,pricing,helpline,offers,payments:allPayments,version:"3.0"};const blob=new Blob([JSON.stringify(data,null,2)],{type:"application/json"});const url=URL.createObjectURL(blob);const a=document.createElement("a");a.href=url;a.download=`dawntodusk_backup_${todayStr()}.json`;a.click();showToast("✅ Backup downloaded!");}} style={{padding:"10px",background:C.primary,border:"none",color:"#fff",borderRadius:9,cursor:"pointer",fontSize:13,fontWeight:700,fontFamily:"inherit"}}>📥 Full Backup (JSON)</button>
              <button onClick={()=>{const csv=["ID,Name,Owner,Phone,City,Plan,Status,Trial End",...tenants.map(t=>`${t.id},"${t.name}","${t.owner_name||""}",${t.phone||""},${t.city||""},${t.plan},${t.status},${t.trial_end}`)].join("\n");const blob=new Blob(["\uFEFF"+csv],{type:"text/csv"});const url=URL.createObjectURL(blob);const a=document.createElement("a");a.href=url;a.download=`tenants_${todayStr()}.csv`;a.click();showToast("✅ CSV downloaded!");}} style={{padding:"10px",background:C.success,border:"none",color:"#fff",borderRadius:9,cursor:"pointer",fontSize:13,fontWeight:700,fontFamily:"inherit"}}>📊 Tenants CSV</button>
              <button onClick={()=>{const csv=["Invoice,Tenant,Amount,Plan,Duration,Method,Date,Expiry",...allPayments.map(p=>`${p.invoice_no},"${p.tenant_name}",${p.amount},${p.plan},${p.duration_months}mo,${p.method},${p.paid_date},${p.expiry_date}`)].join("\n");const blob=new Blob(["\uFEFF"+csv],{type:"text/csv"});const url=URL.createObjectURL(blob);const a=document.createElement("a");a.href=url;a.download=`payments_${todayStr()}.csv`;a.click();showToast("✅ Payments CSV downloaded!");}} style={{padding:"10px",background:C.warning,border:"none",color:"#fff",borderRadius:9,cursor:"pointer",fontSize:13,fontWeight:700,fontFamily:"inherit"}}>💳 Payments CSV</button>
            </div>
          </div>
        </div>}

        {/* ── ANNOUNCE TAB ── */}
        {saTab==="announce"&&<div>
          <div style={{fontSize:14,fontWeight:800,marginBottom:12,color:C.purple}}>📢 Announcements</div>
          <div style={{background:"#fff",borderRadius:12,padding:14,marginBottom:12,border:`1px solid ${C.border}`}}>
            <div style={{fontWeight:700,marginBottom:8}}>Broadcast Message</div>
            <textarea value={announcement} onChange={e=>setAnnouncement(e.target.value)} placeholder="Write a message — it will be sent to all distributors..." style={{...IS,resize:"vertical",minHeight:100,outline:"none",marginBottom:10}}/>
            <button onClick={()=>{if(!announcement.trim())return;const msg=encodeURIComponent(`📢 *DawnToDusk Pro*\n\n${announcement}\n\n📞 ${helpline.phone||""}\n💬 ${helpline.whatsapp||""}\nTeam DawnToDusk Pro`);window.open(`https://wa.me/?text=${msg}`,"_blank");}} style={{width:"100%",padding:"11px",background:"#25D366",border:"none",color:"#fff",borderRadius:9,cursor:"pointer",fontSize:13,fontWeight:700,fontFamily:"inherit"}}>📱 WhatsApp Broadcast</button>
          </div>
          <div style={{fontWeight:700,marginBottom:8}}>Individual Messages:</div>
          {tenants.map(t=>(
            <div key={t.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",background:"#fff",borderRadius:8,padding:"10px 12px",marginBottom:6,border:`1px solid ${C.border}`}}>
              <div><div style={{fontWeight:700,fontSize:12}}>{t.name}</div><div style={{fontSize:11,color:C.sub}}>{t.phone}</div></div>
              <button onClick={()=>{const ph=(t.phone||"").replace(/\D/g,"");if(ph&&announcement)openWA(t.phone,`Dear ${t.owner_name||t.name},\n\n${announcement}\n\n📞 ${helpline.phone||""}\nTeam DawnToDusk Pro`);else if(!announcement)showToast("Please write a message first!","err");}} style={{background:"#25D366",border:"none",color:"#fff",padding:"6px 12px",borderRadius:7,cursor:"pointer",fontSize:11,fontWeight:700}}>📱 Send</button>
            </div>
          ))}
        </div>}

      </div>

      {/* ── BOTTOM NAV ── */}
      <div style={{position:"fixed",bottom:0,left:0,right:0,background:"#fff",borderTop:`1px solid ${C.border}`,display:"flex",zIndex:100,boxShadow:"0 -2px 10px rgba(0,0,0,0.08)"}}>
        {[
          {id:"home",      icon:"🏠", label:"Home"},
          {id:"distributors",icon:"🏢",label:"Dist."},
          {id:"requests",  icon:"📋", label:`Leads${newReqCount>0?` (${newReqCount})`:""}`, urgent:newReqCount>0},
          {id:"revenue",   icon:"💰", label:"Revenue"},
          {id:"settings",  icon:"⚙️", label:"Settings"},
        ].map(t=>(
          <button key={t.id} onClick={()=>setSaTab(t.id)} style={{flex:1,padding:"8px 4px",border:"none",background:"transparent",cursor:"pointer",display:"flex",flexDirection:"column",alignItems:"center",gap:2,fontFamily:"inherit",borderTop:saTab===t.id?`2px solid ${C.primary}`:"2px solid transparent"}}>
            <span style={{fontSize:18}}>{t.icon}</span>
            <span style={{fontSize:9,fontWeight:saTab===t.id?700:400,color:saTab===t.id?C.primary:t.urgent?C.danger:C.sub}}>{t.label}</span>
          </button>
        ))}
      </div>

      {/* ── PAYMENT MODAL ── */}
      {showPayModal&&selTenant&&<div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.6)",zIndex:200,display:"flex",alignItems:"flex-end",justifyContent:"center"}}>
        <div style={{background:"#fff",borderRadius:"20px 20px 0 0",padding:20,width:"100%",maxWidth:500,maxHeight:"90vh",overflowY:"auto"}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
            <div style={{flex:1}}>
              <div style={{fontWeight:800,fontSize:16}}>💰 Payment Record</div>
              <div style={{fontSize:12,color:C.sub}}>{selTenant.name} • {selTenant.owner_name}</div>
              <div style={{display:"flex",gap:8,marginTop:5,flexWrap:"wrap"}}>
                <span style={{fontSize:11,padding:"2px 8px",borderRadius:20,background:(PLANS.find(p=>p.id===selTenant.plan)?.color||C.primary)+"20",color:PLANS.find(p=>p.id===selTenant.plan)?.color||C.primary,fontWeight:700}}>
                  {PLANS.find(p=>p.id===selTenant.plan)?.icon} Current: {selTenant.plan}
                </span>
                {selTenant.trial_end&&(()=>{
                  const dl=daysLeft(selTenant.trial_end);
                  const isFuture=new Date(selTenant.trial_end)>new Date();
                  return <span style={{fontSize:11,padding:"2px 8px",borderRadius:20,background:isFuture?C.success+"20":C.danger+"20",color:isFuture?C.success:C.danger,fontWeight:700}}>
                    📅 {isFuture?`${dl} days left`:dl===0?"Expires today":new Date(selTenant.trial_end).toLocaleDateString("en-IN")}
                  </span>;
                })()}
              </div>
            </div>
            <button onClick={()=>setShowPayModal(false)} style={{background:C.bg,border:"none",borderRadius:7,width:30,height:30,cursor:"pointer",fontSize:16}}>✕</button>
          </div>

          {/* Plan */}
          <div style={{marginBottom:12}}>
            <div style={{fontSize:11,fontWeight:600,color:C.sub,marginBottom:6}}>Select Plan *</div>
            <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
              {PLANS.map(p=>(
                <button key={p.id} onClick={()=>setPayForm({...payForm,plan:p.id,customAmt:""})} style={{flex:1,minWidth:70,padding:"8px 4px",borderRadius:9,border:`2px solid ${payForm.plan===p.id?p.color:C.border}`,background:payForm.plan===p.id?p.color+"15":"#fff",cursor:"pointer",fontFamily:"inherit",fontSize:11,fontWeight:700,color:payForm.plan===p.id?p.color:C.text,textAlign:"center"}}>
                  <div>{p.icon}</div>
                  <div>{p.name}</div>
                  <div style={{fontSize:10,fontWeight:400,color:payForm.plan===p.id?p.color:C.sub}}>₹{pricing[p.id]?.price||p.price}/mo</div>
                </button>
              ))}
            </div>
          </div>

          {/* Duration */}
          <div style={{marginBottom:12}}>
            <div style={{fontSize:11,fontWeight:600,color:C.sub,marginBottom:6}}>Duration *</div>
            <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
              {DURATIONS.map(d=>(
                <button key={d.months} onClick={()=>setPayForm({...payForm,months:d.months,customAmt:""})} style={{flex:1,minWidth:55,padding:"7px 3px",borderRadius:8,border:`2px solid ${payForm.months===d.months?C.primary:C.border}`,background:payForm.months===d.months?"#EFF6FF":"#fff",cursor:"pointer",fontFamily:"inherit",fontSize:10,fontWeight:payForm.months===d.months?700:400,textAlign:"center"}}>
                  {d.label.split(" ")[0]}M
                  {d.disc>0&&<div style={{color:C.success,fontSize:9}}>{d.disc}% off</div>}
                </button>
              ))}
            </div>
          </div>

          {/* Amount */}
          <div style={{background:"#F0FDF4",borderRadius:10,padding:12,marginBottom:12,border:"1px solid #BBF7D0"}}>
            <div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}>
              <span style={{fontSize:12,color:C.sub}}>Calculated Amount:</span>
              <span style={{fontWeight:800,fontSize:18,color:C.success}}>₹{calcAmt(payForm.plan,payForm.months,pricing).toLocaleString("en-IN")}</span>
            </div>
            <div style={{fontSize:11,color:C.sub,marginBottom:8}}>✅ Valid till: {calcExpiry(payForm.months)}</div>
            <Inp l="Custom Amount (if different)" v={payForm.customAmt||""} oc={e=>setPayForm({...payForm,customAmt:e.target.value})} p={`₹${calcAmt(payForm.plan,payForm.months,pricing)} (change if needed)`}/>
          </div>

          {/* Method */}
          <div style={{marginBottom:12}}>
            <div style={{fontSize:11,fontWeight:600,color:C.sub,marginBottom:6}}>Payment Method *</div>
            <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
              {["Cash","UPI","Bank Transfer","Cheque"].map(m=>(
                <button key={m} onClick={()=>setPayForm({...payForm,method:m})} style={{flex:1,padding:"7px 4px",borderRadius:8,border:`2px solid ${payForm.method===m?C.success:C.border}`,background:payForm.method===m?C.success+"15":"#fff",cursor:"pointer",fontFamily:"inherit",fontSize:11,fontWeight:payForm.method===m?700:400,textAlign:"center"}}>
                  {m}
                </button>
              ))}
            </div>
          </div>

          <Inp l="Note / Transaction ID" v={payForm.note||""} oc={e=>setPayForm({...payForm,note:e.target.value})} p="UPI ref: xyz123..."/>

          {/* Summary */}
          {(()=>{
            const newExpiry=calcExpiry(payForm.months, selTenant.trial_end);
            const currExpiry=selTenant.trial_end?new Date(selTenant.trial_end).toLocaleDateString("en-IN"):"—";
            const isFuture=selTenant.trial_end&&new Date(selTenant.trial_end)>new Date();
            const planChanged=selTenant.plan!==payForm.plan&&selTenant.plan!=="trial";
            return <div style={{background:C.bg,borderRadius:9,padding:12,marginBottom:14,fontSize:12}}>
              <div style={{fontWeight:700,marginBottom:6}}>📋 Payment Summary</div>
              <div style={{display:"flex",flexDirection:"column",gap:4}}>
                <div style={{display:"flex",justifyContent:"space-between"}}>
                  <span style={{color:C.sub}}>Distributor</span>
                  <span style={{fontWeight:700}}>{selTenant.name}</span>
                </div>
                <div style={{display:"flex",justifyContent:"space-between"}}>
                  <span style={{color:C.sub}}>Plan</span>
                  <span style={{fontWeight:700,color:PLANS.find(p=>p.id===payForm.plan)?.color}}>
                    {selTenant.plan!==payForm.plan&&selTenant.plan!=="trial"
                      ?<span>{selTenant.plan} → {payForm.plan} <span style={{fontSize:10,background:"#DCFCE7",color:"#16A34A",padding:"1px 5px",borderRadius:4}}>Upgrade</span></span>
                      :<span>{(PLANS.find(p=>p.id===payForm.plan)||{}).name||payForm.plan}</span>
                    }
                  </span>
                </div>
                <div style={{display:"flex",justifyContent:"space-between"}}>
                  <span style={{color:C.sub}}>Duration</span>
                  <span style={{fontWeight:600}}>{payForm.months} month{payForm.months>1?"s":""}</span>
                </div>
                <div style={{display:"flex",justifyContent:"space-between"}}>
                  <span style={{color:C.sub}}>Method</span>
                  <span style={{fontWeight:600}}>{payForm.method}</span>
                </div>
                <div style={{height:1,background:C.border,margin:"4px 0"}}/>
                {isFuture&&<div style={{display:"flex",justifyContent:"space-between"}}>
                  <span style={{color:C.sub}}>Current expiry</span>
                  <span style={{fontWeight:600,color:C.warning}}>{currExpiry}</span>
                </div>}
                <div style={{display:"flex",justifyContent:"space-between"}}>
                  <span style={{color:C.sub}}>{isFuture?"Extended till":"Valid till"}</span>
                  <span style={{fontWeight:700,color:C.success}}>{new Date(newExpiry).toLocaleDateString("en-IN")}</span>
                </div>
                {isFuture&&<div style={{fontSize:10,color:C.info,background:"#EFF6FF",borderRadius:6,padding:"4px 8px"}}>
                  ℹ️ {selTenant.name}'s remaining time won't be wasted — it'll extend from the current expiry
                </div>}
                <div style={{display:"flex",justifyContent:"space-between",marginTop:4}}>
                  <span style={{fontWeight:700,fontSize:14}}>Total Amount</span>
                  <span style={{fontWeight:800,color:C.success,fontSize:16}}>₹{(payForm.customAmt||calcAmt(payForm.plan,payForm.months,pricing)).toLocaleString("en-IN")}</span>
                </div>
              </div>
            </div>;
          })()}

          <button onClick={recordPayment} disabled={loading} style={{width:"100%",padding:"14px",background:loading?"#ccc":C.success,border:"none",color:"#fff",borderRadius:12,cursor:loading?"not-allowed":"pointer",fontSize:14,fontWeight:800,fontFamily:"inherit",boxShadow:"0 4px 12px rgba(16,185,129,0.3)"}}>
            {loading?"⏳ Saving...":"✅ Save + Send WhatsApp Receipt"}
          </button>
        </div>
      </div>}

      {/* ── EDIT MODAL ── */}
      {showEditModal&&editForm.id&&<div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.6)",zIndex:200,display:"flex",alignItems:"center",justifyContent:"center",padding:16}}>
        <div style={{background:"#fff",borderRadius:16,padding:20,width:"100%",maxWidth:460,maxHeight:"90vh",overflowY:"auto"}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
            <div style={{fontWeight:800,fontSize:16}}>✏️ Edit Distributor</div>
            <button onClick={()=>{setShowEditModal(false);setEditForm({});}} style={{background:C.bg,border:"none",borderRadius:7,width:30,height:30,cursor:"pointer",fontSize:16}}>✕</button>
          </div>
          <Inp l="Firm Name" v={editForm.name||""} oc={e=>setEditForm({...editForm,name:e.target.value})}/>
          <Inp l="Owner Name" v={editForm.owner_name||""} oc={e=>setEditForm({...editForm,owner_name:e.target.value})}/>
          <Inp l="Phone" v={editForm.phone||""} oc={e=>setEditForm({...editForm,phone:e.target.value})}/>
          <Inp l="City" v={editForm.city||""} oc={e=>setEditForm({...editForm,city:e.target.value})}/>
          <Inp l="Email" v={editForm.email||""} oc={e=>setEditForm({...editForm,email:e.target.value})}/>
          <div style={{marginBottom:10}}>
            <div style={{fontSize:11,fontWeight:600,color:C.sub,marginBottom:4}}>New Password (khali chhodo agar change nahi karna)</div>
            <input type="password" value={editForm.newPassword||""} onChange={e=>setEditForm({...editForm,newPassword:e.target.value})} placeholder="Naya password..." style={{width:"100%",padding:"10px 12px",borderRadius:9,border:`1.5px solid ${C.border}`,fontSize:13,fontFamily:"inherit",outline:"none",background:"#F8FAFC",boxSizing:"border-box"}}/>
          </div>
          <div style={{marginBottom:10}}>
            <div style={{fontSize:11,fontWeight:600,color:C.sub,marginBottom:4}}>Status</div>
            <select value={editForm.status||"active"} onChange={e=>setEditForm({...editForm,status:e.target.value})} style={{...IS}}>
              <option value="active">✅ Active</option>
              <option value="inactive">⏸ Inactive</option>
              <option value="expired">⏰ Expired</option>
            </select>
          </div>
          <div style={{marginBottom:10}}>
            <div style={{fontSize:11,fontWeight:600,color:C.sub,marginBottom:4}}>Plan</div>
            <select value={editForm.plan||"trial"} onChange={e=>setEditForm({...editForm,plan:e.target.value})} style={{...IS}}>
              <option value="trial">🆓 Trial</option>
              {PLANS.map(p=><option key={p.id} value={p.id}>{p.icon} {p.name}</option>)}
            </select>
          </div>
          <div style={{marginBottom:14}}>
            <div style={{fontSize:11,fontWeight:600,color:C.sub,marginBottom:4}}>Valid Till / Expiry Date</div>
            <input type="date" value={editForm.trial_end||""} onChange={e=>setEditForm({...editForm,trial_end:e.target.value})} style={{...IS}}/>
          </div>
          <div style={{display:"flex",gap:10}}>
            <button onClick={()=>{setShowEditModal(false);setEditForm({});}} style={{flex:1,padding:"11px",background:C.bg,border:`1px solid ${C.border}`,borderRadius:9,cursor:"pointer",fontSize:13,fontWeight:700,fontFamily:"inherit"}}>Cancel</button>
            <button onClick={saveTenantEdit} disabled={loading} style={{flex:2,padding:"11px",background:loading?"#ccc":C.primary,border:"none",color:"#fff",borderRadius:9,cursor:loading?"not-allowed":"pointer",fontSize:13,fontWeight:700,fontFamily:"inherit"}}>
              {loading?"⏳ Saving...":"💾 Save Changes"}
            </button>
          </div>
        </div>
      </div>}

      {/* ── PROFILE MODAL ── */}
      {showProfileModal&&selTenant&&<div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.6)",zIndex:200,display:"flex",alignItems:"flex-end",justifyContent:"center"}}>
        <div style={{background:"#fff",borderRadius:"20px 20px 0 0",padding:20,width:"100%",maxWidth:500,maxHeight:"90vh",overflowY:"auto"}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
            <div style={{fontWeight:800,fontSize:16}}>👁️ Distributor Profile</div>
            <button onClick={()=>setShowProfileModal(false)} style={{background:C.bg,border:"none",borderRadius:7,width:30,height:30,cursor:"pointer",fontSize:16}}>✕</button>
          </div>
          {/* Info */}
          <div style={{background:C.bg,borderRadius:12,padding:14,marginBottom:12}}>
            <div style={{fontWeight:800,fontSize:16,marginBottom:4}}>{selTenant.name}</div>
            <div style={{fontSize:12,color:C.sub}}>👤 {selTenant.owner_name}</div>
            <div style={{fontSize:12,color:C.sub}}>📞 {selTenant.phone}</div>
            <div style={{fontSize:12,color:C.sub}}>🏙️ {selTenant.city||"—"}</div>
            <div style={{fontSize:12,color:C.sub}}>📧 {selTenant.email||"—"}</div>
            <div style={{display:"flex",gap:8,marginTop:8}}>
              <span style={{fontSize:11,padding:"3px 10px",borderRadius:20,background:selTenant.status==="active"?C.success+"20":C.danger+"20",color:selTenant.status==="active"?C.success:C.danger,fontWeight:700}}>{selTenant.status}</span>
              <span style={{fontSize:11,padding:"3px 10px",borderRadius:20,background:C.primary+"20",color:C.primary,fontWeight:700}}>{selTenant.plan}</span>
            </div>
            <div style={{marginTop:6,fontSize:11,color:C.sub}}>Valid till: {new Date(selTenant.trial_end).toLocaleDateString("en-IN")} ({daysLeft(selTenant.trial_end)} din baaki)</div>
          </div>
          {/* Stats */}
          {tenantStats[selTenant.id]&&<div style={{background:"#fff",borderRadius:12,padding:14,marginBottom:12,border:`1px solid ${C.border}`}}>
            <div style={{fontWeight:700,marginBottom:8}}>📊 Usage Stats</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
              {[
                {l:"Orders",v:tenantStats[selTenant.id].orders,c:C.primary},
                {l:"Customers",v:tenantStats[selTenant.id].customers,c:C.success},
                {l:"Products",v:tenantStats[selTenant.id].products,c:C.warning},
                {l:"Staff",v:tenantStats[selTenant.id].users,c:C.info},
              ].map(s=>(
                <div key={s.l} style={{background:C.bg,borderRadius:8,padding:"8px 10px",textAlign:"center"}}>
                  <div style={{fontSize:18,fontWeight:800,color:s.c}}>{s.v}</div>
                  <div style={{fontSize:11,color:C.sub}}>{s.l}</div>
                </div>
              ))}
            </div>
            {tenantStats[selTenant.id].lastLogin&&<div style={{marginTop:8,fontSize:11,color:C.sub}}>🕐 Last Login: {new Date(tenantStats[selTenant.id].lastLogin).toLocaleString("en-IN",{timeZone:"Asia/Kolkata"})}</div>}
          </div>}
          {/* Payment History */}
          {payHistory[selTenant.id]&&<div style={{background:"#fff",borderRadius:12,padding:14,marginBottom:12,border:`1px solid ${C.border}`}}>
            <div style={{fontWeight:700,marginBottom:8}}>💳 Payment History</div>
            {payHistory[selTenant.id].length===0?<div style={{color:C.sub,fontSize:12,textAlign:"center"}}>Koi payment nahi</div>:
            payHistory[selTenant.id].map(p=>(
              <div key={p.id} style={{display:"flex",justifyContent:"space-between",padding:"6px 0",borderBottom:`1px solid ${C.border}`,fontSize:12}}>
                <div>
                  <div style={{fontWeight:700}}>{p.invoice_no}</div>
                  <div style={{color:C.sub,fontSize:10}}>{p.plan} • {p.duration_months}mo • {p.method} • {new Date(p.paid_date).toLocaleDateString("en-IN")}</div>
                </div>
                <div style={{fontWeight:800,color:C.success}}>₹{fmt(p.amount)}</div>
              </div>
            ))}
          </div>}
          {/* Quick Actions */}
          <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
            <button onClick={()=>{setShowProfileModal(false);setPayForm({plan:selTenant.plan==="trial"?"basic":selTenant.plan,months:1,method:"Cash",note:"",customAmt:""});setShowPayModal(true);}} style={{flex:2,padding:"10px",background:C.success,border:"none",color:"#fff",borderRadius:9,cursor:"pointer",fontSize:12,fontWeight:700,fontFamily:"inherit"}}>💰 Payment</button>
            <button onClick={()=>{setShowProfileModal(false);setEditForm({...selTenant});setShowEditModal(true);}} style={{flex:1,padding:"10px",background:C.primary,border:"none",color:"#fff",borderRadius:9,cursor:"pointer",fontSize:12,fontWeight:700,fontFamily:"inherit"}}>✏️ Edit</button>
            <button onClick={()=>openWA(selTenant.phone,WA_TEMPLATES[selTenant.plan==="trial"?"trial":"renewal"](selTenant,daysLeft(selTenant.trial_end),helpline))} style={{padding:"10px 14px",background:"#25D366",border:"none",color:"#fff",borderRadius:9,cursor:"pointer",fontSize:12,fontWeight:700}}>📱</button>
          </div>
        </div>
      </div>}

    </div>
  );
}
